import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Car, Gauge, Settings as SettingsIcon, Upload, ChevronRight, ChevronLeft, ChevronDown,
  Check, CheckCircle2, Clock, Rocket, AlertCircle, AlertTriangle, Wallet, X, ZoomIn, FileText, Wand2, Loader2,
  MessageSquare, Paperclip, ArrowUp, Sparkles, Zap, Search,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { calculatePrice, getRoundingStep, applyRounding } from '../lib/pricingCalculator';
import { savePendingFile, readPendingFile, clearPendingFiles } from '../utils/pendingFiles';
import PdfInstructionViewer from './PdfInstructionViewer';
import { pickFinalPriceBarMode } from './finalPriceBarLogic';
import LegalDocModal from './LegalDocModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ───────── Static selectors kept identical to the original wizard ─────────
// Gearbox types are admin-editable via /api/config/gearbox-types — this
// constant is only used as a last-ditch fallback if the network call fails.
const GEARBOX_TYPES_FALLBACK = [
  'Manual', '5M', '6M', '7M',
  'Automatic transmission',
  'DSG', 'DSG6 DQ250', 'DSG7 DQ200', 'DSG7 DQ200 MQB', 'DSG7 DQ250 MQB',
  'DSG7 DQ381 MQB', 'DSG7 DQ500',
  'Multitronic', 'Porsche PDK Gearbox',
  'S-Tronic', 'S-Tronic DL501', 'S-Tronic DSG DQ800',
  'Tiptronic', 'ZF AL551', 'ZF AL552', 'CVT',
  'Getrag GS7D36BG BMW M5/M3',
];
const MODEL_YEARS = Array.from({ length: 30 }, (_, i) => (2025 - i).toString());
const FILE_TYPES = ['File is original', 'File is modified', 'File with fault'];
const READING_METHODS_FALLBACK = ['CMD Tricore Bootload', 'KTAG', 'Kess V2', 'PCMFlash', 'BitBox', 'Other'];
const PRIORITIES = [
  { id: 'highest', name: 'Highest', time: '10-30 min',  extra: '+20%', color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-300' },
  { id: 'high',    name: 'High',    time: '30-90 min',  extra: '',     color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-300' },
  { id: 'medium',  name: 'Medium',  time: '1-3 hours',  extra: '',     color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-300' },
  { id: 'low',     name: 'Low',     time: '3-6 hours',  extra: '',     color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-300' },
];

// Abbreviation for the circular option icon (like "DPF", "EGR", "S1"…)
const abbrev = (name) => {
  if (!name) return '?';
  const up = name.toUpperCase();
  const caps = up.match(/[A-Z0-9]+/g) || [];
  if (!caps.length) return up.slice(0, 3);
  const joined = caps.join('');
  // Prefer an uppercase acronym up to 4 chars
  return joined.slice(0, 4);
};

// Convert bare URLs in option instruction text into clickable <a> tags.
// Admins occasionally paste a raw URL (e.g. "see https://auto-remap.com/...")
// instead of using the editor's link button — without this helper the URL
// would render as plain text. We skip URLs that already live inside an <a
// href> attribute by only matching outside of HTML tags.
const linkify = (html) => {
  if (!html) return '';
  // Split on tags so we only transform plain text segments.
  const URL_RE = /(\bhttps?:\/\/[^\s<>"']+)/gi;
  return html.split(/(<[^>]+>)/g).map((seg) => {
    if (seg.startsWith('<')) return seg;
    return seg.replace(URL_RE, (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer">${m}</a>`);
  }).join('');
};

// Mapping from `tuning_options.key` → static PNG icon path. Files are stored
// under /app/frontend/public/icons/options/ and pulled from auto-remap.com so
// the order page matches the public site's visual identity. Unmapped keys
// fall back to a coloured-circle abbreviation (e.g. "CHKS" for checksum).
import { OPTION_ICONS } from '../utils/optionIcons';

import SafeHtml from './SafeHtml';
import MyVehiclesStrip from './MyVehiclesStrip';
const OptionIcon = ({ optionKey, label, selected }) => {
  const file = OPTION_ICONS[optionKey];
  if (file) {
    return (
      <div
        className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 overflow-hidden ${
          selected ? 'bg-cyan-500 border-cyan-500' : 'bg-white border-cyan-400'
        }`}
      >
        <img
          src={`/icons/options/${file}`}
          alt=""
          className={`w-7 h-7 object-contain transition-all ${selected ? 'brightness-0 invert' : ''}`}
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div
      className={`w-11 h-11 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all flex-shrink-0 ${
        selected ? 'bg-cyan-500 border-cyan-500 text-white' : 'bg-cyan-50 border-cyan-400 text-cyan-600'
      }`}
    >
      {label}
    </div>
  );
};

// ─────────── Step 2 — compact Final price bar (desktop / tablet) ───────────
// Single source of truth: reads from the `pricePreview` DTO returned by
// `POST /api/orders/price-preview`. Renders a one-line summary directly
// under the stepper. Detailed breakdown stays in the right-side Summary.
const FinalPriceBar = ({
  previewLoading, pricePreview,
  selectedCount, finalTotal, standardPrice, balanceBefore,
  inline = false, isAdmin = false,
}) => {
  const { t: rawT, i18n } = useTranslation('new-order');
  // Admin area is strictly English regardless of operator's UI language
  // preference — pin translations to English when isAdmin.
  const t = isAdmin ? i18n.getFixedT('en', 'new-order') : rawT;
  const hasDiscount = !!(pricePreview && pricePreview.has_customer_discount);
  const pct = hasDiscount ? Math.round(pricePreview.customer_discount_percent || 0) : 0;
  const groupName = hasDiscount ? (pricePreview.customer_group_name || 'tariff') : null;
  const isEmpty = selectedCount === 0;
  const balanceAfter = (balanceBefore != null && finalTotal >= 0)
    ? balanceBefore - finalTotal
    : null;
  // When rendered inline (right of the stepper) the parent row already
  // handles the bottom margin; standalone usage keeps `mb-4` so the
  // bar floats above the form content.
  const wrapperMb = inline ? '' : 'mb-4';

  // Single source of truth for what visual variant to render. Per UX
  // request, the customer-facing bar never surfaces a price-update
  // warning — `previewError` is intentionally not consumed here. The
  // 409 from /orders/* is logged via console.warn in handleSubmit
  // for debugging; the right Summary keeps mirroring the same DTO.
  const mode = pickFinalPriceBarMode({
    previewError: null, previewLoading, selectedCount, finalTotal,
  });

  // Loading path
  if (mode === 'loading') {
    return (
      <div
        data-testid="final-price-bar"
        className={`${wrapperMb} px-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-500 text-sm flex items-center gap-2`}
      >
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
        <span data-testid="final-price-bar-loading">{t('updating_price', { defaultValue: 'Updating price…' })}</span>
      </div>
    );
  }

  return (
    <div
      data-testid="final-price-bar"
      className={`${wrapperMb} px-4 py-2.5 rounded-lg border shadow-sm ${
        isEmpty
          ? 'bg-slate-50 border-slate-200'
          : hasDiscount
            ? 'bg-emerald-50/60 border-emerald-300'
            : 'bg-cyan-50/60 border-cyan-300'
      }`}
    >
      {isEmpty ? (
        <span className="text-sm text-slate-500 flex items-baseline gap-2" data-testid="final-price-bar-empty">
          {t('no_services_selected', { defaultValue: 'No services selected' })}
          <span className="text-slate-300">·</span>
          <span>{t('final_price', { defaultValue: 'Final price:' })}&nbsp;
            <strong className="text-slate-700" data-testid="final-price-bar-amount">0 EUR</strong>
          </span>
        </span>
      ) : (
        <div className={`flex flex-col ${inline ? 'lg:items-end' : ''}`}>
          {/* Row 1: label only */}
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {t('final_amount_for_payment', { defaultValue: 'Final amount for payment' })}
          </span>
          {/* Row 2: BIG amount + struck-through standard price + tariff pill */}
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mt-0.5">
            <strong
              className="text-3xl font-black text-slate-900 leading-none"
              data-testid="final-price-bar-amount"
            >
              {Math.round(finalTotal || 0)} EUR
            </strong>
            {hasDiscount && Math.round(standardPrice) > Math.round(finalTotal) && (
              <span
                className="text-xs text-slate-400 line-through whitespace-nowrap"
                data-testid="final-price-bar-standard"
              >
                {t('standard_price', { defaultValue: 'standard price' })}&nbsp;{Math.round(standardPrice)} EUR
              </span>
            )}
            {hasDiscount && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold whitespace-nowrap"
                data-testid="final-price-bar-tariff"
                title={groupName || ''}
              >
                {t('your_tariff', { pct, defaultValue: `Your tariff: -${pct}%` })}
              </span>
            )}
          </div>
          {/* Row 3: small, quiet balance line */}
          {balanceAfter !== null && (
            <span
              className={`mt-1 text-[11px] ${
                balanceAfter < 0 ? 'text-rose-600' : 'text-slate-500'
              } whitespace-nowrap`}
              data-testid="final-price-bar-balance"
            >
              {t('summary.balance_after')}:&nbsp;
              <strong>{balanceAfter.toFixed(2)} EUR</strong>
            </span>
          )}
          {/* The previous amber "Price was updated — please review
              before submitting." badge was removed per UX feedback —
              the bar's job is to show the final number unambiguously.
              The price-update signal is still logged to the console
              (`handleSubmit` → console.warn) for debugging and the
              backend keeps returning 409 `price_changed` for admin
              audit. */}
        </div>
      )}
    </div>
  );
};

// ─────────── Step 2 — sticky bottom price bar (mobile ≤ lg) ───────────
// Compact, single-row: count · final · action. Hidden on lg+ where the
// top FinalPriceBar + right-side Summary serve the same purpose.
const MobileStickyBar = ({
  pricePreview, previewLoading, selectedCount,
  finalTotal, standardPrice, isReady, missingCount, ctaLabel, onSubmit,
  isAdmin = false,
}) => {
  const { t: rawT, i18n } = useTranslation('new-order');
  // See FinalPriceBar — admin sees English regardless of UI language.
  const t = isAdmin ? i18n.getFixedT('en', 'new-order') : rawT;
  const hasDiscount = !!(pricePreview && pricePreview.has_customer_discount);
  const loading = previewLoading && selectedCount > 0;
  return (
    <div
      data-testid="mobile-sticky-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-6px_16px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="px-3 py-2.5 flex items-center gap-3">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            {selectedCount === 0
              ? t('mobile_bar.no_services', { defaultValue: 'No services' })
              : loading
                ? t('mobile_bar.updating', { defaultValue: 'Updating…' })
                : t('mobile_bar.n_services', { count: selectedCount, defaultValue: `${selectedCount} service${selectedCount > 1 ? 's' : ''}` })}
          </span>
          <span className="flex items-baseline gap-1.5">
            <strong
              className="text-lg font-black text-slate-900"
              data-testid="mobile-sticky-bar-amount"
            >
              {Math.round(finalTotal || 0)} EUR
            </strong>
            {hasDiscount && Math.round(standardPrice) > Math.round(finalTotal) && (
              <span className="text-[10px] text-slate-400 line-through">
                {Math.round(standardPrice)}
              </span>
            )}
          </span>
        </div>
        <button
          type="button"
          data-testid="mobile-sticky-bar-cta"
          onClick={isReady ? onSubmit : undefined}
          disabled={!isReady}
          className={`flex-shrink-0 px-4 py-2.5 rounded-lg font-bold text-sm transition-all ${
            isReady
              ? 'bg-cyan-500 text-white hover:bg-cyan-400'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
          }`}
        >
          {ctaLabel}
        </button>
      </div>
      {missingCount > 0 && (
        <div className="px-3 pb-2 -mt-1 text-[11px] text-amber-700" data-testid="mobile-sticky-bar-missing">
          {t('mobile_bar.n_fields_required', { count: missingCount, defaultValue: `${missingCount} field${missingCount > 1 ? 's' : ''} required` })}
        </div>
      )}
    </div>
  );
};

export default function NewOrderWithPricing({ user, token, onOrderCreated, onNavigate, editingOrder = null, onCloseEdit, isAdmin = false }) {
  const { t: rawT, i18n } = useTranslation('new-order');
  // Admin area is strictly English. When this wizard is opened from
  // the admin "Edit order" overlay (isAdmin=true), pin every t() call
  // (and any nested helper component below that re-uses the same
  // namespace) to English so the operator never sees the customer's
  // chosen UI language while they're editing on the customer's behalf.
  const t = isAdmin ? i18n.getFixedT('en', 'new-order') : rawT;
  // Editing existing order? Drives copy ("Save changes" vs "Submit") and PUT vs POST.
  const isEditMode = Boolean(editingOrder);
  // ────── Vehicle fields ──────
  const [vehicleType, setVehicleType] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [engine, setEngine] = useState('');
  const [ecuType, setEcuType] = useState('');
  const [gearbox, setGearbox] = useState('');
  const [enginePower, setEnginePower] = useState({ hp: '', kw: '' });
  const [modelYear, setModelYear] = useState('');
  const [vin, setVin] = useState('');

  // ────── Smart Vehicle Request (legacy state — kept for back-compat ──────
  // The old free-form "Smart Vehicle Request" flow has been REPLACED by
  // the ECU-first model finder below ("Find by ECU"). The state vars are
  // still here because the backend's `smart_vehicle_request=true` flag
  // and Step-2 `/smart-vehicle/options` endpoint are referenced elsewhere
  // (admin promotion modal, legacy orders). New orders never flip
  // `smart=true` — the substitute model picked via the ECU finder is a
  // real catalog model so it flows through the regular pipeline.
  // ────── Smart Vehicle Request — RETIRED (2026-09-28) ──────
  // The free-form "my vehicle isn't in the catalog" flow was removed: a
  // scripted signup used it to submit a €0 order. The backend now rejects
  // `smart_vehicle_request=true` outright. The constant stays false so the
  // remaining legacy JSX branches keep compiling, and nothing can enable it.
  const smart = false;
  const [smartOpen, setSmartOpen] = useState(false);                // ECU finder panel expanded
  // Variant-B escape hatch: when the customer explicitly opted out of
  // the ECU-first flow ("I don't know the ECU — pick model first"), we
  // remember that choice for the rest of the session so the ECU finder
  // doesn't auto-reopen every time they tweak the manufacturer.
  // Never persisted — reset on page reload, next order restarts the
  // default ECU-first flow.
  const [userPrefModelFirst, setUserPrefModelFirst] = useState(false);
  const [smartBrandEcus, setSmartBrandEcus] = useState([]);         // ECU options for this brand
  const [smartEcusLoading, setSmartEcusLoading] = useState(false);

  // Admin-editable hint shown under the Model dropdown. Loaded from
  // /api/config/texts → key `ecu_finder_hint`. Empty string falls back
  // to the built-in i18n default so the customer always sees SOMETHING.
  const [ecuFinderHint, setEcuFinderHint] = useState('');

  // ────── Find-model-by-ECU substitute picker ──────
  // The customer can't find their exact model → they pick the ECU they
  // physically see on the vehicle, and we suggest every other catalog
  // model that ships with that same ECU for the current vehicle_type
  // + brand. Picking one fills `model` and `ecuType` as if they had
  // selected it via the regular dropdowns — the rest of the form (engine
  // dropdown, pricing, options) works without any special-case logic.
  const [subEcu, setSubEcu] = useState('');                         // chosen ECU in the substitute flow
  const [subModels, setSubModels] = useState([]);                   // models found for that ECU
  const [subModelsLoading, setSubModelsLoading] = useState(false);
  // New 3-step Find-by-ECU cascade state (spec §6.5–6.10). Auto-resolved
  // when count == 1 at any level. The user must click "Apply and continue"
  // before any of these values are written to the main form fields.
  const [subModel, setSubModel] = useState('');                     // chosen substitute model
  const [subEngines, setSubEngines] = useState([]);                 // engines for chosen ECU+model
  const [subEnginesLoading, setSubEnginesLoading] = useState(false);
  const [subEngine, setSubEngine] = useState('');                   // chosen substitute engine

  // ─── ECU-Finder usage telemetry ────────────────────────────────────
  // Latches True the moment the customer presses "Apply and continue"
  // in the Find-by-ECU helper. Persisted into the order payload as
  // `created_via_ecu_finder` so the admin order view can render a
  // small wand badge. Never cleared once set — re-opening / changing
  // fields manually keeps the badge on, since the order still
  // originated from a smart-selector path.
  const [usedEcuFinder, setUsedEcuFinder] = useState(false);

  // ────── Service fields ──────
  const [selectedOptions, setSelectedOptions] = useState([]); // array of option keys
  const [fileType, setFileType] = useState('');
  const [readingMethod, setReadingMethod] = useState('');
  const [readingMethods, setReadingMethods] = useState(READING_METHODS_FALLBACK);
  // Gearbox types — fetched from admin-editable list, with a static
  // fallback so the dropdown always renders something even if the
  // endpoint is unreachable on first load.
  const [gearboxTypes, setGearboxTypes] = useState(GEARBOX_TYPES_FALLBACK);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/reading-methods`);
        if (!r.ok || stop) return;
        const data = await r.json();
        if (Array.isArray(data) && data.length) setReadingMethods(data);
      } catch (_) { /* silent */ }
    })();
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/gearbox-types`);
        if (!r.ok || stop) return;
        const data = await r.json();
        if (Array.isArray(data) && data.length) setGearboxTypes(data);
      } catch (_) { /* silent */ }
    })();
    return () => { stop = true; };
  }, []);
  const [priority, setPriority] = useState('');
  const [apiPriorities, setApiPriorities] = useState(null); // null=loading, []=no data, populated array
  const [workingHours, setWorkingHours] = useState(null);
  const [tickNow, setTickNow] = useState(Date.now());
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRefund, setAcceptRefund] = useState(false);
  // Slug of the legal doc currently shown in the popup ('terms' | 'refund' | null).
  const [legalSlug, setLegalSlug] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  // ────── Optional customer note (description) ──────
  // Free-text message + optional reference photo the customer wants the
  // engineer to see. Both fields are optional and never block submission.
  const [customerNote, setCustomerNote] = useState('');
  const [notePhoto, setNotePhoto] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [pdfInstruction, setPdfInstruction] = useState(null);  // {url, name}
  // Success modal shown after order create — replaces the old browser alert.
  // Shape: { number, total, queuePosition, queueTotal, etaIso } | null
  const [orderSuccess, setOrderSuccess] = useState(null);
  // Friendly "not enough funds" modal shown when the order would push the
  // balance below zero. Shape: { needed, balance, shortfall } | null
  const [insufficientFunds, setInsufficientFunds] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');

  // ────── UI state ──────
  const [activeTab, setActiveTab] = useState('vehicle');
  const [submitting, setSubmitting] = useState(false);
  const [decodingVin, setDecodingVin] = useState(false);
  const [vinMessage, setVinMessage] = useState(null); // { tone: 'ok'|'warn'|'err', text }

  // ────── Dynamic options from Pricing DB ──────
  const [types, setTypes] = useState([]);
  const [typeCounts, setTypeCounts] = useState({}); // { Cars: 12847, Agriculture: 412, ... }
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  const [ecus, setEcus] = useState([]);
  const [priceOptions, setPriceOptions] = useState({}); // { key: {name, price, description} }
  // Holds the saved selectedOptions while waiting for `priceOptions` to load
  // during a sessionStorage-backed restore. The [ecuType] effect resets
  // selectedOptions to [], so we re-apply from this ref immediately after.
  const pendingRestoreOptionsRef = useRef(null);
  const [roundingStep, setRoundingStep] = useState(0); // 0 = no rounding
  const [pricingRules, setPricingRules] = useState([]); // admin-configured rules
  // Customer discount (from group assignment) — computed server-side from totalPrice
  const [discountInfo, setDiscountInfo] = useState(null);  // {base, discount, final, has_discount, group_name, ...}

  // ────── Server-side price preview ──────
  // Single source of truth for the Step 2 "Final amount for payment" bar,
  // the right-side Summary, the submit `total_price` and the backend
  // order amount. Refetched on every change to selected_options /
  // priority / vehicle context. While in flight, the top bar shows
  // "Updating price..." and the submit button is gated.
  const [pricePreview, setPricePreview] = useState(null);   // server DTO
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Server time ticker is driven by tickNow + workingHoursInfo above (see below).

  // ────── Fetchers ──────
  // IMPORTANT: must send the JWT so price endpoints can read the user's
  // `price_coefficient` and apply it (base × global × hours × user_coef).
  // Without this header `/api/price-catalog/options` returns ANONYMOUS
  // pricing and the personal coefficient (e.g. 1.1) silently has no effect.
  const fetchJson = useCallback(async (path) => {
    const res = await fetch(`${API_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const raw = await res.text();
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Price catalog source switched from MongoDB to Supabase (May 2026).
      // The cascade endpoints now live under /api/price-catalog/*.
      const [d, counts] = await Promise.all([
        fetchJson('/api/price-catalog/vehicle-types'),
        // Per-category headcount for the dropdown hint. Falls back to
        // an empty map on failure so the picker still works without
        // numbers if the endpoint is unreachable.
        fetchJson('/api/price-catalog/vehicle-type-counts').catch(() => ({})),
      ]);
      if (cancelled) return;
      setTypes(Array.isArray(d) ? d : []);
      setTypeCounts((counts && typeof counts === 'object' && !Array.isArray(counts)) ? counts : {});
    };
    load();
    // Refresh the headcount badge whenever the tab regains focus so
    // an admin price push (Phase D incremental: ≤ 10 min) is visible
    // to the customer without a full page reload.
    const onFocus = () => { load(); };
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [fetchJson]);

  // Load the public config knobs (rules + rounding step). Auth not required.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/config/pricing-rules`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        setPricingRules(Array.isArray(data) ? data : []);
        setRoundingStep(getRoundingStep(data));
      } catch (_) { /* silent */ }
    })();
  }, [token]);

  // Global service flags — show maintenance banner if orders are disabled.
  const [globalFlags, setGlobalFlags] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/service/flags`);
        if (r.ok) setGlobalFlags(await r.json());
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Prefill state from editingOrder once on mount (or when editingOrder changes).
  useEffect(() => {
    if (!editingOrder) return;
    setVehicleType(editingOrder.vehicle_type || '');
    setManufacturer(editingOrder.manufacturer || '');
    setModel(editingOrder.model || '');
    setEngine(editingOrder.engine || '');
    setEcuType(editingOrder.ecu_type || '');
    setGearbox(editingOrder.gearbox || '');
    setEnginePower({
      hp: editingOrder.engine_power_hp != null ? String(editingOrder.engine_power_hp) : '',
      kw: editingOrder.engine_power_kw != null ? String(editingOrder.engine_power_kw) : '',
    });
    // Model Year prefill — with a fallback for legacy orders.
    //
    // Older orders (and some imports) didn't store `model_year` as a
    // dedicated field — the year lived inside the engine string,
    // typically as the last pipe-separated segment, e.g.:
    //     "2.0 TDI 6MT | | Diesel | 2011"
    //                                ^^^^ → 2011
    // Without this fallback the Model Year dropdown shows empty in
    // the Edit Order form and the admin has to re-pick the year manually
    // (and validation blocks "Go to options" until they do).
    let preYear = editingOrder.model_year != null ? String(editingOrder.model_year) : '';
    if (!preYear && typeof editingOrder.engine === 'string') {
      const segs = editingOrder.engine.split('|').map(s => s.trim()).filter(Boolean);
      // walk from the end — the year is usually the last 4-digit token
      for (let i = segs.length - 1; i >= 0; i -= 1) {
        const m = segs[i].match(/\b(19|20)\d{2}\b/);
        if (m) { preYear = m[0]; break; }
      }
    }
    setModelYear(preYear);
    setVin(editingOrder.vin || '');
    // selectedOptions: defer until priceOptions for this ECU has loaded.
    // The [ecuType] effect resets selectedOptions to [] right after this
    // restore runs, so we stash the saved choices in a ref and re-apply
    // them once the price list has been fetched (same pattern as
    // sessionStorage restore below).
    if (Array.isArray(editingOrder.selected_options) && editingOrder.selected_options.length > 0) {
      pendingRestoreOptionsRef.current = editingOrder.selected_options;
    } else {
      setSelectedOptions([]);
    }
    setFileType(editingOrder.file_type || '');
    setReadingMethod(editingOrder.reading_method || '');
    setPriority(editingOrder.priority || '');
    setCustomerNote(editingOrder.customer_note || '');
    setAcceptTerms(true);
    setAcceptRefund(true);
    // file is left empty — user can upload a new one to replace
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingOrder?.id]);

  // Restore a "pending" order that was abandoned by the customer when they
  // were redirected to top up their wallet. Once we've consumed it we wipe
  // the storage so a manual "New order" button click later starts fresh.
  // Only runs once per mount and ignores stale entries (>2h old).
  useEffect(() => {
    if (editingOrder) return;
    let raw = null;
    try { raw = sessionStorage.getItem('pendingOrder'); } catch (e) { /* ignore */ }
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      if (!p || (Date.now() - (p.ts || 0) > 2 * 3600_000)) {
        sessionStorage.removeItem('pendingOrder');
        return;
      }
      if (p.vehicleType)    setVehicleType(p.vehicleType);
      if (p.manufacturer)   setManufacturer(p.manufacturer);
      if (p.model)          setModel(p.model);
      if (p.engine)         setEngine(p.engine);
      if (p.ecuType || p.ecu) setEcuType(p.ecuType || p.ecu);
      if (p.gearbox)        setGearbox(p.gearbox);
      if (p.enginePower)    setEnginePower(p.enginePower);
      if (p.modelYear)      setModelYear(p.modelYear);
      if (p.vin)            setVin(p.vin);
      if (p.fileType)       setFileType(p.fileType);
      if (p.readingMethod)  setReadingMethod(p.readingMethod);
      // selectedOptions can NOT be applied right now — the [ecuType] effect
      // below (which fetches the per-vehicle prices) clears them after this
      // restore runs. We stash them in a ref and apply them once
      // `priceOptions` has loaded for the restored ECU.
      if (Array.isArray(p.selectedOptions) && p.selectedOptions.length > 0) {
        pendingRestoreOptionsRef.current = p.selectedOptions;
      }
      if (p.priority)       setPriority(p.priority);
      if (p.acceptTerms)    setAcceptTerms(true);
      if (p.acceptRefund)   setAcceptRefund(true);
      if (p.customerNote) {
        setCustomerNote(p.customerNote);
        // Auto-open the collapsible Description block so the customer
        // immediately sees their text without having to click the summary.
        setTimeout(() => {
          const det = document.querySelector('[data-testid="customer-note-details"]');
          if (det) det.setAttribute('open', '');
        }, 0);
      }
      // Auto-jump to step 2 so the customer lands on the "Place order"
      // button instead of having to navigate back through Vehicle → Options.
      if (Array.isArray(p.selectedOptions) && p.selectedOptions.length > 0) {
        setActiveTab('services');
      }
      // The actual file blob may have been stashed in IndexedDB during the
      // "Insufficient funds → top up" flow. Try to rehydrate both the
      // original tuning file and the customer-note photo so the customer
      // doesn't have to re-attach them.
      (async () => {
        try {
          const [orig, photo] = await Promise.all([
            readPendingFile('uploadedFile'),
            readPendingFile('notePhoto'),
          ]);
          // The IDB store returns either a Blob or a File depending on what
          // was passed in. We coerce Blob → File so the FormData submit and
          // the "selected: <name>" UI stay consistent.
          const toFile = (blob, meta) => {
            if (!blob) return null;
            if (blob instanceof File) return blob;
            return new File([blob], meta?.name || 'file', { type: blob.type });
          };
          const f1 = toFile(orig,  p.uploadedFile);
          const f2 = toFile(photo, p.notePhoto);
          if (f1) setUploadedFile(f1);
          if (f2) setNotePhoto(f2);
          // Only show the "please re-attach" toast for files we couldn't
          // recover. If we got everything back, show a clean success toast.
          const stillMissing = [
            !f1 && p.uploadedFile?.name,
            !f2 && p.notePhoto?.name,
          ].filter(Boolean);
          import('sonner').then(({ toast }) => {
            if (stillMissing.length > 0) {
              toast.info(t('order_restored.title', { defaultValue: 'Order restored' }), {
                description: `${t('order_restored.desc', { defaultValue: 'Your previous order details and files were restored after recharge.' })} ${stillMissing.join(', ')}`,
                duration: 10000,
              });
            } else {
              toast.success(t('order_restored.title', { defaultValue: 'Order restored' }), {
                description: t('order_restored.desc', { defaultValue: 'Your previous order details and files were restored after recharge.' }),
                duration: 6000,
              });
            }
          }).catch(() => { /* noop */ });
        } catch (e) { /* IDB unavailable — fall back to manual re-attach */ }
      })();
      sessionStorage.removeItem('pendingOrder');
    } catch (e) {
      sessionStorage.removeItem('pendingOrder');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load priorities from API (admin can disable / schedule them).
  // Server-side per-customer disables (`user.disabled_priorities`) are also
  // filtered here so the UI never offers a priority that the backend would
  // reject with 403.
  //
  // Mobile-quirk guard: Safari on iOS sometimes drops the Authorization
  // header on 307 redirects. The fetch below uses the canonical URL with
  // trailing slash AND retries once without credentials if the first
  // attempt returned 401/0 — this is what previously made the Priority
  // list silently empty for the customer on their phone.
  useEffect(() => {
    let cancelled = false;
    const doFetch = async (url) => fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'omit',
    });
    const loadPrios = async () => {
      try {
        let res = await doFetch(`${API_URL}/api/priorities/`);
        if (!res.ok && res.status !== 401) {
          // Transient 5xx — give it one immediate retry before giving up.
          await new Promise((r) => setTimeout(r, 400));
          res = await doFetch(`${API_URL}/api/priorities/`);
        }
        if (!res.ok) {
          if (!cancelled) setApiPriorities((prev) => prev || []);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const blocked = new Set((user?.disabled_priorities) || []);
        const filtered = (Array.isArray(data) ? data : []).filter((p) => !blocked.has(p.key));
        setApiPriorities(filtered);
        // Default selection rule (when no priority is chosen yet and we are
        // not editing an existing order): only auto-select if there is
        // exactly ONE priority available right now — that's the off-hours
        // case where, say, only "Lowest" is open and pre-selecting it saves
        // the customer a click. During working hours we leave the choice
        // empty so the customer EXPLICITLY clicks the speed they want —
        // otherwise we were nudging everyone into "high" by default which
        // made them place orders faster than they really needed.
        if (!priority) {
          const availableNow = filtered.filter((p) => p.available_now);
          if (availableNow.length === 1) {
            setPriority(availableNow[0].key);
          }
          // else: leave unselected — Step 2 validator already forces the
          // user to click one before "Continue" becomes enabled.
        }
      } catch (_) {
        // Keep previous value rather than flashing "No priorities" if the
        // network blipped mid-session.
        if (!cancelled) setApiPriorities((prev) => prev || []);
      }
    };
    loadPrios();
    const id = setInterval(loadPrios, 60000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.disabled_priorities]);

  // Load working hours config (public endpoint)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/working-hours`);
        if (!r.ok) return;
        setWorkingHours(await r.json());
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Tick every second to refresh banner countdown / priority availability boundaries
  useEffect(() => {
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute today's working hours window + countdown to close (in minutes)
  const workingHoursInfo = useMemo(() => {
    if (!workingHours) return null;
    const offsetH = Number(workingHours.timezone_offset ?? 3);
    const nowUtc = new Date(tickNow);
    const local = new Date(nowUtc.getTime() + offsetH * 3600000);
    const weekday = (local.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
    const day = (workingHours.days || []).find((d) => Number(d.day) === weekday);
    if (!day || day.closed) return { closed: true, weekday, open: null, close: null, minutesToClose: 0, currentMin: local.getUTCHours() * 60 + local.getUTCMinutes() };
    const [oh, om] = String(day.open || '07:00').split(':').map((x) => parseInt(x, 10) || 0);
    const [ch, cm] = String(day.close || '23:00').split(':').map((x) => parseInt(x, 10) || 0);
    const openMin = oh * 60 + om;
    const closeMin = ch * 60 + cm;
    const currentMin = local.getUTCHours() * 60 + local.getUTCMinutes();
    return {
      closed: false,
      weekday,
      open: day.open,
      close: day.close,
      openMin,
      closeMin,
      currentMin,
      minutesToClose: closeMin - currentMin,
      minutesToOpen: openMin - currentMin,
    };
  }, [workingHours, tickNow]);

  const fmtCountdown = (mins) => {
    if (mins <= 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  useEffect(() => {
    if (!vehicleType) { setManufacturers([]); return; }
    (async () => {
      const d = await fetchJson(`/api/price-catalog/brands?vehicle_type=${encodeURIComponent(vehicleType)}`);
      setManufacturers(Array.isArray(d) ? d : []);
    })();
  }, [vehicleType, fetchJson]);

  // Fetch the admin-editable ECU-finder hint (config_texts). Public
  // endpoint — no auth needed. Empty/missing → component will fall back
  // to the built-in i18n default in the render path below.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/texts`);
        if (!r.ok) return;
        const data = await r.json();
        const v = data && data.ecu_finder_hint;
        if (typeof v === 'string') setEcuFinderHint(v);
      } catch (_) { /* silent — fallback to i18n */ }
    })();
  }, []);

  useEffect(() => {
    if (!manufacturer) { setModels([]); return; }
    (async () => {
      const d = await fetchJson(
        `/api/price-catalog/models?vehicle_type=${encodeURIComponent(vehicleType)}&brand=${encodeURIComponent(manufacturer)}`
      );
      setModels(Array.isArray(d) ? d : []);
    })();
  }, [manufacturer, vehicleType, fetchJson]);

  // Variant-B: auto-open the Find-by-ECU helper the moment the customer
  // has narrowed the price catalog to (vehicle_type + manufacturer).
  // 100% of the customer base reads the ECU label off the physical
  // control unit, so surfacing the ECU picker up front is faster than
  // scrolling a 200-row model list. The classic Model dropdown remains
  // accessible via the "pick model first" escape link inside the helper.
  //
  // Guards:
  //  - Skip when the model/ecu are already filled (edit-order, "Quick
  //    start from my vehicles" card, sessionStorage restore, VIN decode).
  //  - Skip when the customer opted out this session (userPrefModelFirst).
  //  - Skip when the helper was manually closed (smartOpen already false
  //    AND they've re-picked manufacturer — a fresh brand change should
  //    reopen unless they've clicked escape).
  useEffect(() => {
    if (!manufacturer) { setSmartOpen(false); return; }
    if (model || ecuType) return;                 // already resolved
    if (userPrefModelFirst) return;               // customer opted out
    setSmartOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturer]);

  useEffect(() => {
    if (!model) { setEngines([]); return; }
    (async () => {
      const d = await fetchJson(
        `/api/price-catalog/engines?vehicle_type=${encodeURIComponent(vehicleType)}&brand=${encodeURIComponent(manufacturer)}&model=${encodeURIComponent(model)}`
      );
      setEngines(Array.isArray(d) ? d : []);
    })();
  }, [model, manufacturer, vehicleType, fetchJson]);

  useEffect(() => {
    if (!engine) { setEcus([]); return; }
    (async () => {
      const d = await fetchJson(
        `/api/price-catalog/ecu-types?vehicle_type=${encodeURIComponent(vehicleType)}&brand=${encodeURIComponent(manufacturer)}&model=${encodeURIComponent(model)}&engine=${encodeURIComponent(engine)}`
      );
      const list = Array.isArray(d) ? d : [];
      setEcus(list);
      // Auto-pick if only one ECU is available for the chosen engine
      if (list.length === 1) setEcuType(list[0]);
    })();
  }, [engine, model, manufacturer, vehicleType, fetchJson]);

  // Load priced options once the ECU is known. The retired Smart Vehicle
  // Request branch (brand+ecu_type matcher) has been removed — every order
  // now prices against a real catalog model/engine row.
  useEffect(() => {
    if (!ecuType) { setPriceOptions({}); setSelectedOptions([]); return; }
    (async () => {
      const d = await fetchJson(
        `/api/price-catalog/options?vehicle_type=${encodeURIComponent(vehicleType)}&brand=${encodeURIComponent(manufacturer)}&model=${encodeURIComponent(model)}&engine=${encodeURIComponent(engine)}&ecu_type=${encodeURIComponent(ecuType)}`
      );
      // Map API rows → legacy dict shape so existing wizard maths
      // (tuningList, selectedOptions, pricing) keeps working. Preserve
      // `option_type` so the Step 2 picker can split into a primary
      // engine-ECU block + a "Gearbox tuning options:" subsection.
      const dict = {};
      if (Array.isArray(d)) {
        for (const row of d) {
          if (!row || !row.option_key) continue;
          dict[row.option_key] = {
            name: row.option_name || row.option_key,
            price: Number(row.price) || 0,
            description: row.info || '',
            option_type: row.option_type || 'engine',
          };
        }
      }
      setPriceOptions(dict);
      setSelectedOptions([]);
    })();
  }, [smart, ecuType, engine, model, manufacturer, vehicleType, fetchJson]);

  // ────── Smart Vehicle Request: load every published ECU for this brand ──────
  // Fires when the panel is opened so the dropdown is ready instantly.
  useEffect(() => {
    if (!smartOpen || !manufacturer) { setSmartBrandEcus([]); return; }
    setSmartEcusLoading(true);
    (async () => {
      try {
        const q = new URLSearchParams({ brand: manufacturer });
        if (vehicleType) q.set('vehicle_type', vehicleType);
        const d = await fetchJson(`/api/price-catalog/ecu-types-by-brand?${q}`);
        setSmartBrandEcus(Array.isArray(d) ? d : []);
      } catch (_) { setSmartBrandEcus([]); }
      finally { setSmartEcusLoading(false); }
    })();
  }, [smartOpen, manufacturer, vehicleType, fetchJson]);

  // ────── Find-model-by-ECU: load model suggestions for the picked ECU ──────
  // Fires when the customer picks an ECU in the substitute panel. We hit
  // GET /price-catalog/models-by-ecu which returns every catalog model
  // that ships with this exact (vehicle_type + brand + ecu_type) triple.
  useEffect(() => {
    if (!smartOpen || !subEcu || !manufacturer || !vehicleType) {
      setSubModels([]);
      return;
    }
    setSubModelsLoading(true);
    (async () => {
      try {
        const q = new URLSearchParams({
          vehicle_type: vehicleType,
          brand:        manufacturer,
          ecu_type:     subEcu,
        });
        const d = await fetchJson(`/api/price-catalog/models-by-ecu?${q}`);
        const list = Array.isArray(d) ? d : [];
        setSubModels(list);
        // Spec §6.6 — auto-resolve when only one model matches the ECU.
        // Skip the model dropdown entirely and let the engine step take
        // focus. Reset whenever the ECU changes (handled by Step 1's
        // onChange that already clears subModel/subEngine).
        if (list.length === 1) setSubModel(list[0]);
      } catch (_) { setSubModels([]); }
      finally { setSubModelsLoading(false); }
    })();
  }, [smartOpen, subEcu, manufacturer, vehicleType, fetchJson]);

  // ────── Find-model-by-ECU step 3: load engines for ECU + model ──────
  // After the customer (or auto-resolution) picks a substitute model we
  // load every engine that ships with this (vt + brand + model + ecu_type)
  // quadruple. Reuses the price-catalog/engines endpoint with the new
  // ecu_type filter introduced in iter-100. If only one engine matches we
  // auto-resolve straight to the summary card (spec §6.7).
  useEffect(() => {
    if (!smartOpen || !subEcu || !subModel || !manufacturer || !vehicleType) {
      setSubEngines([]); setSubEngine('');
      return;
    }
    setSubEnginesLoading(true);
    (async () => {
      try {
        const q = new URLSearchParams({
          vehicle_type: vehicleType,
          brand:        manufacturer,
          model:        subModel,
          ecu_type:     subEcu,
        });
        const d = await fetchJson(`/api/price-catalog/engines?${q}`);
        const list = Array.isArray(d) ? d : [];
        setSubEngines(list);
        if (list.length === 1) setSubEngine(list[0]);
        else                   setSubEngine('');
      } catch (_) { setSubEngines([]); setSubEngine(''); }
      finally { setSubEnginesLoading(false); }
    })();
  }, [smartOpen, subEcu, subModel, manufacturer, vehicleType, fetchJson]);

  // Once priceOptions has loaded for the restored ECU, re-apply the
  // selectedOptions that were stashed during the sessionStorage restore.
  // We filter to only keys that exist in the current price list so a stale
  // option (e.g. removed by admin between restore + return) is silently
  // dropped instead of erroring out at submit time.
  useEffect(() => {
    if (!pendingRestoreOptionsRef.current) return;
    if (!priceOptions || Object.keys(priceOptions).length === 0) return;
    const valid = pendingRestoreOptionsRef.current.filter((k) => k in priceOptions);
    if (valid.length > 0) setSelectedOptions(valid);
    pendingRestoreOptionsRef.current = null;
  }, [priceOptions]);

  // ────── Derived state ──────
  const missingFields = useMemo(() => {
    const miss = [];
    if (!vehicleType) miss.push(t('fields.vehicle_type', { defaultValue: 'Vehicle Type' }));
    if (!manufacturer) miss.push(t('fields.manufacturer', { defaultValue: 'Manufacturer' }));
    if (!model) miss.push(t('fields.model', { defaultValue: 'Model' }));
    if (!engine) miss.push(t('fields.engine', { defaultValue: 'Engine' }));
    if (!ecuType) miss.push(t('fields.ecu_type', { defaultValue: 'ECU Type' }));
    if (!gearbox) miss.push(t('fields.gearbox_type', { defaultValue: 'Gearbox Type' }));
    if (!modelYear) miss.push(t('fields.model_year', { defaultValue: 'Model Year' }));
    if (!enginePower.hp && !enginePower.kw) miss.push(t('fields.engine_power', { defaultValue: 'Engine Power' }));
    return miss;
  }, [vehicleType, manufacturer, model, engine, ecuType, gearbox, modelYear, enginePower, t]);

  const isVehicleValid = missingFields.length === 0;

  // Build displayable tuning options from the per-vehicle pricing dict
  const tuningList = useMemo(() => {
    return Object.entries(priceOptions).map(([key, opt]) => ({
      id: key,
      icon: abbrev(opt.name),
      name: opt.name,
      price: Number(opt.price) || 0,
      instruction: opt.description || '',
      image: null,
      option_type: opt.option_type || 'engine',
    }));
  }, [priceOptions]);

  // Split for the Step 2 picker: engine-ECU options stay in the main
  // list, TCU options group under a dedicated "Gearbox tuning options:"
  // subheading — matches the legacy site layout the operator asked for.
  const engineList = useMemo(
    () => tuningList.filter((o) => o.option_type !== 'tcu'),
    [tuningList],
  );
  const gearboxList = useMemo(
    () => tuningList.filter((o) => o.option_type === 'tcu'),
    [tuningList],
  );

  const selectedOptionsInfo = useMemo(
    () => selectedOptions.map((id) => tuningList.find((o) => o.id === id)).filter(Boolean),
    [selectedOptions, tuningList]
  );

  // ────── Photo-instructions per selected option ──────
  // We hit /api/instructions/match for each (vehicle, option) pair currently
  // selected. The response is cached by a composite key so toggling options
  // back and forth doesn't re-fetch. Failures are silent — text-only
  // instructions still render.
  const [optionImages, setOptionImages] = useState({}); // { optionId: [{image_url, text_override}] }
  useEffect(() => {
    let canc = false;
    if (!manufacturer || selectedOptions.length === 0) { setOptionImages({}); return; }
    const need = selectedOptions.filter((id) => !optionImages[id]);
    if (need.length === 0) return;
    (async () => {
      const additions = {};
      await Promise.all(need.map(async (oid) => {
        try {
          const p = new URLSearchParams({
            manufacturer, model: model || '', engine: engine || '',
            ecu_type: ecuType || '', vehicle_type: vehicleType || '',
            option_slug: oid,
          });
          const r = await fetch(`${API_URL}/api/instructions/match?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
          if (!r.ok) return;
          const d = await r.json();
          additions[oid] = d.items || [];
        } catch (_) { additions[oid] = []; }
      }));
      if (!canc && Object.keys(additions).length) {
        setOptionImages((prev) => ({ ...prev, ...additions }));
      }
    })();
    return () => { canc = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, manufacturer, model, engine, ecuType, vehicleType]);

  // Rebuild selectedOptionsInfo with matching photo instructions merged in.
  const selectedOptionsInfoWithImages = useMemo(() => {
    return selectedOptionsInfo.map((opt) => {
      const matches = optionImages[opt.id] || [];
      if (matches.length === 0) return opt;
      // Prefer text override from the instruction when present; otherwise
      // keep the textual hint coming from the price database.
      const override = matches.find((m) => m.text_override)?.text_override;
      const withPdf = matches.find((m) => m.pdf_url);
      return {
        ...opt,
        instruction: override || opt.instruction,
        // Pick the first matching image. (Stack rendering can be added later
        // if more than one image per option-vehicle pair becomes common.)
        image: opt.image || (matches.find((m) => m.image_url) ? `${API_URL}${matches.find((m) => m.image_url).image_url}` : null),
        pdf: withPdf ? { url: `${API_URL}${withPdf.pdf_url}`, name: withPdf.pdf_name || 'Instruction.pdf' } : null,
      };
    });
  }, [selectedOptionsInfo, optionImages]);

  const servicesMissing = useMemo(() => {
    const miss = [];
    if (selectedOptions.length === 0) miss.push(t('missing.service', { defaultValue: 'At least one service' }));
    // In edit mode the original file already lives on the server; don't
    // force the admin/client to re-upload unless they want to replace it.
    if (!uploadedFile && !(isEditMode && editingOrder?.original_file_name)) miss.push(t('missing.upload', { defaultValue: 'Upload file' }));
    if (!readingMethod) miss.push(t('missing.reading_method', { defaultValue: 'Reading method' }));
    if (!fileType) miss.push(t('missing.file_type', { defaultValue: 'File type' }));
    if (!priority) miss.push(t('missing.priority', { defaultValue: 'Priority' }));
    return miss;
  }, [selectedOptions, uploadedFile, readingMethod, fileType, priority, isEditMode, editingOrder, t]);

  // ────────────── Priority i18n helpers ──────────────
  // Priorities come from the API with English `name` / `description`. We
  // localise them client-side keyed by the slug (`p.key`) so non-EN
  // customers see translated labels. Admin custom priorities (with keys
  // we don't ship translations for) keep the original API text.
  const localisePriorityName = useCallback((p) => {
    if (!p || !p.key) return p?.name || '';
    const translated = t(`priorities.${p.key}.name`, { defaultValue: '' });
    return translated || p.name || '';
  }, [t]);
  const localisePriorityDescription = useCallback((p) => {
    if (!p || !p.key) return p?.description || '';
    const translated = t(`priorities.${p.key}.description`, { defaultValue: '' });
    return translated || p.description || '';
  }, [t]);
  // Admin-managed `unavailable_reason` is one of two patterns generated
  // server-side:
  //   • "Disabled HH:MM-HH:MM"   (priority schedule disabled window)
  //   • "Hidden N min before close (HH:MM)"  (auto-hide window before close)
  // We pattern-match each and rebuild from translation keys.
  const localiseUnavailableReason = useCallback((reason) => {
    if (!reason) return '';
    const m1 = /^Disabled\s+(\d{1,2}:\d{2})-(\d{1,2}:\d{2})\s*$/i.exec(reason);
    if (m1) return t('priorities.disabled_window', { from: m1[1], to: m1[2], defaultValue: reason });
    const m2 = /^Hidden\s+(\d+)\s+min\s+before\s+close\s*\((\d{1,2}:\d{2})\)\s*$/i.exec(reason);
    if (m2) return t('priorities.hidden_before_close', { minutes: m2[1], close: m2[2], defaultValue: reason });
    return reason;
  }, [t]);

  const isServicesValid = servicesMissing.length === 0 && acceptTerms && acceptRefund;

  const pricingInfo = useMemo(() => {
    if (selectedOptionsInfo.length === 0) {
      return { items: [], subtotal: 0, savings: 0, originalTotal: 0, appliedRules: [] };
    }
    return calculatePrice(selectedOptionsInfo, pricingRules);
  }, [selectedOptionsInfo, pricingRules]);

  const totalPrice = useMemo(() => {
    let price = pricingInfo.subtotal;
    const apiP = (apiPriorities || []).find((x) => x.key === priority);
    if (apiP && apiP.factor && apiP.factor !== 1) price *= apiP.factor;
    return applyRounding(price, roundingStep);
  }, [pricingInfo.subtotal, priority, roundingStep, apiPriorities]);

  // ────── Server price preview ──────
  // Calls the canonical `/api/orders/price-preview` so the displayed
  // Final amount, the right Summary number and the submit `total_price`
  // are all driven by ONE backend calculation. Refetched whenever the
  // customer changes the selection / priority / vehicle. Debounced to
  // avoid hammering the endpoint while the user clicks fast.
  useEffect(() => {
    // Need: a token (auth-required endpoint), the vehicle context AND
    // at least one selected option. Otherwise we just reset the preview
    // so the bar shows "No services selected · Final: 0 EUR".
    if (!token || !ecuType || !manufacturer || !vehicleType) {
      setPricePreview(null); setPreviewLoading(false); setPreviewError(null);
      return;
    }
    if (!selectedOptions || selectedOptions.length === 0) {
      setPricePreview(null); setPreviewLoading(false); setPreviewError(null);
      return;
    }
    setPreviewLoading(true); setPreviewError(null);
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/price-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            vehicle_type: vehicleType,
            manufacturer,
            model: model,
            engine: engine,
            ecu_type: ecuType,
            smart_vehicle_request: false,
            selected_options: selectedOptions,
            priority: priority || null,
            // When admin opens the edit dialog, the price-preview must use
            // the customer's tariff (their `price_coefficient` and
            // `customer_group_id`) — NOT the admin's. Passing the order_id
            // tells the backend to resolve the owner. Ignored for
            // first-party (customer editing their own order).
            order_id: isEditMode && editingOrder?.id ? editingOrder.id : null,
          }),
          signal: ctl.signal,
        });
        if (!r.ok) {
          setPreviewError(`HTTP ${r.status}`);
          setPricePreview(null);
        } else {
          setPricePreview(await r.json());
        }
      } catch (e) {
        if (e?.name !== 'AbortError') { setPreviewError(String(e?.message || e)); setPricePreview(null); }
      } finally {
        setPreviewLoading(false);
      }
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [token, vehicleType, manufacturer, model, engine, ecuType, smart,
      selectedOptions, priority]);

  // Mirror the server's customer-group discount into `discountInfo`
  // so any code that already reads it (Summary card, badges) keeps
  // working without a second network call.
  useEffect(() => {
    if (!pricePreview) { setDiscountInfo(null); return; }
    if (!pricePreview.has_customer_discount) { setDiscountInfo(null); return; }
    setDiscountInfo({
      base: pricePreview.standard_price,
      discount: pricePreview.customer_discount_amount,
      final: pricePreview.final_total,
      has_discount: true,
      group_name: pricePreview.customer_group_name,
      group_type: pricePreview.customer_group_type,
      percent_value: pricePreview.customer_discount_percent,
    });
  }, [pricePreview]);

  // Effective payable amount — server-truthful when the preview has
  // loaded; falls back to the FE estimate so we never render a blank
  // amount while the first request is in flight.
  const effectiveTotal = (pricePreview && typeof pricePreview.final_total === 'number')
    ? pricePreview.final_total
    : ((discountInfo && discountInfo.has_discount) ? discountInfo.final : totalPrice);

  // What the "(standard price: X EUR)" label shows when a customer
  // tariff discount is applied — pulled from the same server DTO.
  const standardPrice = (pricePreview && typeof pricePreview.standard_price === 'number')
    ? pricePreview.standard_price
    : totalPrice;

  // ────── Handlers ──────
  const toggleOption = (id) =>
    setSelectedOptions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handlePowerChange = (field, value) => {
    const num = parseFloat(value) || 0;
    if (field === 'hp') setEnginePower({ hp: value, kw: num ? Math.round(num * 0.7457).toString() : '' });
    else setEnginePower({ hp: num ? Math.round(num / 0.7457).toString() : '', kw: value });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer?.files?.[0] || e.target.files?.[0];
    if (f) setUploadedFile(f);
  };

  const handleTabChange = (t) => { if (t === 'services' && !isVehicleValid) return; setActiveTab(t); };

  const handleDecodeVin = async () => {
    const v = (vin || '').trim().toUpperCase();
    if (!v) { setVinMessage({ tone: 'warn', text: 'Enter a VIN first' }); return; }
    if (v.length !== 17) { setVinMessage({ tone: 'err', text: 'VIN must be exactly 17 characters' }); return; }
    setDecodingVin(true);
    setVinMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/pricing/decode-vin/${encodeURIComponent(v)}`);
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      if (!data.valid) {
        setVinMessage({ tone: 'warn', text: data.message || 'VIN could not be decoded' });
        return;
      }

      // Apply matched fields in order (type → brand → model) and reset downstream
      const m = data.match || {};
      const appliedParts = [];
      if (m.vehicle_type) { setVehicleType(m.vehicle_type); appliedParts.push(`type: ${m.vehicle_type}`); }
      if (m.brand)        { setManufacturer(m.brand);        appliedParts.push(`brand: ${m.brand}`); }
      if (m.model)        { setModel(m.model);                appliedParts.push(`model: ${m.model}`); }
      // Always reset engine/ECU since we don't know those from the VIN
      setEngine(''); setEcuType('');
      if (data.year) setModelYear(String(data.year));

      // Build a helpful summary message
      const rawInfo = [data.make, data.model, data.year, data.trim].filter(Boolean).join(' · ');
      if (appliedParts.length === 0) {
        setVinMessage({
          tone: 'warn',
          text: `Decoded ${rawInfo || data.vin}, but no matching vehicle in our Price DB. Please pick manually.`,
        });
      } else {
        const missing = [];
        if (!m.vehicle_type) missing.push('type');
        if (!m.brand)        missing.push('brand');
        if (!m.model)        missing.push('model');
        const tail = missing.length
          ? ` — ${missing.join(', ')} not matched, please pick manually.`
          : ' — now pick the engine.';
        setVinMessage({ tone: 'ok', text: `Decoded: ${rawInfo}. Auto-filled ${appliedParts.join(', ')}.${tail}` });
        // §5.2 of the spec — brief success toast at the top of the form
        // after a successful VIN decode. Auto-dismisses after 3s.
        try {
          const { toast } = await import('sonner');
          toast.success(t('vin_callout.decoded_toast'), { duration: 3000 });
        } catch (_) { /* sonner optional */ }
      }
    } catch (e) {
      setVinMessage({ tone: 'err', text: 'VIN decode failed: ' + (e?.message || String(e)) });
    } finally {
      setDecodingVin(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        vehicle_type: vehicleType,
        manufacturer,
        model,
        engine,
        ecu_type: ecuType,
        gearbox,
        engine_power_hp: enginePower.hp ? parseInt(enginePower.hp, 10) : null,
        engine_power_kw: enginePower.kw ? parseInt(enginePower.kw, 10) : null,
        model_year: modelYear ? parseInt(modelYear, 10) : null,
        vin: vin || null,
        // Smart Vehicle Request flag + free-text fields. Server uses
        // these to brand+ecu-match and to render the admin badge.
        smart_vehicle_request: false,
        manual_vehicle_model: null,
        manual_vehicle_engine: null,
        // Telemetry — set when the customer used "Find by ECU".
        // Pure observability flag; pricing untouched.
        created_via_ecu_finder: usedEcuFinder,
        selected_options: selectedOptions,
        file_type: fileType,
        reading_method: readingMethod,
        priority,
        subtotal: pricingInfo.subtotal,
        discount: pricingInfo.savings,
        // Server is the source of truth — prefer the live preview number.
        // Falls back to the FE estimate only if the preview never loaded
        // (degraded path). Backend re-validates either way (409 on drift).
        total_price: (pricePreview && typeof pricePreview.final_total === 'number')
          ? pricePreview.final_total
          : effectiveTotal,
        customer_note: customerNote.trim().slice(0, 200) || null,
      };

      // Multipart submit so the original tuning file is attached to the order
      const fd = new FormData();
      fd.append('payload', JSON.stringify(body));
      if (uploadedFile) fd.append('file', uploadedFile);
      if (notePhoto) fd.append('note_photo', notePhoto);

      // Use XHR — fetch + extension-injected proxies have been flaky for FormData uploads
      const url = isEditMode
        ? `${API_URL}/api/orders/${editingOrder.id}/edit`
        : `${API_URL}/api/orders/`;
      const method = isEditMode ? 'PUT' : 'POST';
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 120000;
        xhr.onload = () => resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, text: xhr.responseText });
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.send(fd);
      });
      const raw = res.text || '';
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch (_) {}
      // 409 = backend re-validated the price and got a different number
      // (admin changed the global multiplier / customer coef / catalog
      // price mid-flight). The customer-facing UI no longer renders a
      // warning (UX request: never hide / never replace the final
      // amount, never throw a popup). We silently refresh the preview
      // with the server's new DTO so the FinalPriceBar + right Summary
      // + submit payload all pick up the new number — the customer's
      // next click on Submit posts the matching total. The 409 is
      // still logged to the console for debugging and the backend
      // keeps the 409 contract for admin audit.
      if (res.status === 409) {
        const detail = (data && typeof data.detail === 'object') ? data.detail : null;
        if (detail?.code === 'price_changed') {
          if (detail.price_preview) setPricePreview(detail.price_preview);
          console.warn(
            '[order/submit] price_changed — refreshing preview with server DTO',
            detail.price_preview,
          );
          setPreviewError(null);  // never surfaced to customers
          setSubmitting(false);
          return;  // abort this submit silently; next click will use the new total
        }
      }
      if (!res.ok) throw new Error(
        (data && typeof data.detail === 'string' ? data.detail : null)
          || (data && data.detail && data.detail.message)
          || raw
          || `HTTP ${res.status}`
      );

      // Order accepted — wipe any pending-order leftovers from the recharge
      // resume flow so a fresh order starts with an empty form/files state.
      try {
        sessionStorage.removeItem('pendingOrder');
        sessionStorage.removeItem('pendingTopupAmount');
        await clearPendingFiles();
      } catch (e) { /* ignore */ }

      // Edit mode: simpler success path — toast + close back to list, no
      // queue/eta/celebration modal (the order is already in the queue).
      if (isEditMode) {
        onOrderCreated && onOrderCreated();
        if (onCloseEdit) onCloseEdit({ saved: true });
        return;
      }

      // Success — open the celebratory modal. The user picks Dashboard or
      // continues placing another order from there.
      // Best-effort: pull live queue position + ETA window derived from the
      // selected priority's configured eta_min/eta_max (shown as "1–3h" etc).
      let queuePosition = null;
      let queueTotal = null;
      let etaMin = null;
      let etaMax = null;
      try {
        const q = await fetch(`${API_URL}/api/orders/queue-count`, { headers: { Authorization: `Bearer ${token}` } });
        if (q.ok) {
          const qd = await q.json();
          queueTotal = qd?.queue || null;
        }
      } catch (_) { /* silent */ }
      // Prefer live priority config (admin-managed window)
      let priorityName = null;
      let priorityIcon = null;
      try {
        const selectedPrio = (apiPriorities || []).find((p) => p.key === priority);
        if (selectedPrio) {
          etaMin = Number.isFinite(selectedPrio.eta_min) ? selectedPrio.eta_min : null;
          etaMax = Number.isFinite(selectedPrio.eta_max) ? selectedPrio.eta_max : null;
          priorityName = localisePriorityName(selectedPrio) || selectedPrio.label || priority;
          priorityIcon = selectedPrio.icon || null;
        } else if (priority) {
          // Try i18n key first (handles built-in slugs like 'highest'/'low');
          // fall back to capitalised slug for custom unknown priorities.
          const translated = t(`priorities.${priority}.name`, { defaultValue: '' });
          priorityName = translated || (priority.charAt(0).toUpperCase() + priority.slice(1));
        }
      } catch (_) { /* silent */ }
      if (queueTotal != null) queuePosition = queueTotal;

      setOrderSuccess({
        number: data?.order_number || '',
        total: Number(effectiveTotal) || 0,
        queuePosition,
        queueTotal,
        etaMin,
        etaMax,
        priorityName,
        priorityIcon,
      });
      // Reset wizard state for the next order.
      setSelectedOptions([]); setPriority(''); setAcceptTerms(false); setAcceptRefund(false);
      setUploadedFile(null); setFileType(''); setReadingMethod('');
      setCustomerNote(''); setNotePhoto(null);
      setActiveTab('vehicle');
      onOrderCreated && onOrderCreated();
    } catch (e) {
      const msg = e?.message || String(e);
      // Detect the dedicated insufficient-balance case so we can show a
      // rich "top up your balance" CTA instead of a raw browser alert.
      if (/insufficient\s*balance/i.test(msg)) {
        const balance = Number(user?.balance) || 0;
        const needed = Number(effectiveTotal) || 0;
        const shortfall = Math.max(0, needed - balance);
        setInsufficientFunds({
          balance, needed, shortfall,
        });
        // Pre-fill the editable input with the smallest sensible amount —
        // the exact shortfall. The customer can bump it up via the chips
        // or type any number ≥ shortfall.
        setTopupAmount(shortfall.toFixed(2));
      } else {
        alert('Error creating order: ' + msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ────── Render ──────
  return (
    <div className="max-w-[88rem] mx-auto p-3 sm:p-4 md:p-6" data-testid="new-order-page">
      {/* Edit-mode header */}
      {isEditMode && (
        <div className="rounded-xl mb-4 p-4 bg-gradient-to-r from-rose-500 to-pink-600 text-white flex items-center justify-between" data-testid="edit-mode-banner">
          <div>
            <p className="text-xs uppercase font-bold tracking-widest opacity-90 mb-0.5">Editing order</p>
            <p className="text-base font-bold">№{editingOrder.order_number || editingOrder.id?.slice(0, 8)}</p>
            <p className="text-xs opacity-90 mt-0.5">
              Changes will overwrite the original. Reservation amount will be recalculated.
            </p>
          </div>
          {onCloseEdit && (
            <button
              type="button"
              onClick={() => onCloseEdit({ saved: false })}
              data-testid="edit-mode-close"
              className="px-4 py-2 bg-white/15 hover:bg-white/25 rounded-lg text-sm font-bold inline-flex items-center gap-1.5"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>
      )}

      {/* Service Status Banner */}
      {workingHoursInfo && (() => {
        const open = !workingHoursInfo.closed && workingHoursInfo.minutesToOpen <= 0 && workingHoursInfo.minutesToClose > 0;
        const local = new Date(Date.now() + Number(workingHours?.timezone_offset ?? 3) * 3600000);
        const currentLabel = local.toUTCString().replace(/^\w+, /, '').replace(' GMT', '');
        const tzLabel = `GMT${(workingHours?.timezone_offset ?? 3) >= 0 ? '+' : ''}${workingHours?.timezone_offset ?? 3}`;
        const closeStr = workingHoursInfo.close || '23:00';
        const openStr = workingHoursInfo.open || '07:00';
        const remainMin = Math.max(0, workingHoursInfo.minutesToClose);
        const hh = String(Math.floor(remainMin / 60)).padStart(2, '0');
        const mm = String(remainMin % 60).padStart(2, '0');
        const remainingLabel = `${hh}:${mm}:00`;
        return (
          <div className={`rounded-xl p-4 mb-6 text-white ${open ? 'bg-gradient-to-r from-cyan-500 to-cyan-400' : 'bg-gradient-to-r from-rose-500 to-rose-400'}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-semibold mb-1">
                  {workingHoursInfo.closed
                    ? 'File service is closed today'
                    : open
                      ? t('banner.open_now', { defaultValue: 'File service is open now' })
                      : workingHoursInfo.minutesToOpen > 0
                        ? t('banner.opens_at', { time: openStr, defaultValue: `Service opens at ${openStr}` })
                        : t('banner.closed_today', { defaultValue: 'File service is closed for today' })}
                </div>
                <div className="text-sm opacity-90">
                  {workingHoursInfo.closed
                    ? t('banner.day_off', { defaultValue: 'Day off' })
                    : t('banner.today_working_time', { open: openStr, close: closeStr, tz: tzLabel, defaultValue: `Today working time: ${openStr} – ${closeStr} ${tzLabel}` })}
                </div>
                <div className="text-sm opacity-90">{t('banner.current_time', { defaultValue: 'Current time' })}: {currentLabel} {tzLabel}</div>
              </div>
              {!workingHoursInfo.closed && open && (
                <div className="text-right">
                  <div className="text-sm opacity-90">{t('banner.remaining_to_close', { defaultValue: 'Remaining time to close' })}</div>
                  <div className="text-2xl font-bold" data-testid="remaining-to-close">{remainingLabel}</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Final amount banner moved INTO the right Summary panel (Step 2)
          to avoid the duplicated "Final amount for payment" that field
          testers found confusing. The banner used to render here too —
          per the polish brief we now show it once, where the customer
          actually commits to paying. */}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* Numbered step indicator + inline Final-price bar. On lg+ the
            bar sits to the RIGHT of the stepper in the same row (per
            updated TZ — saves vertical space, fills the empty area to
            the right of the stepper). On narrower viewports the bar
            wraps below the stepper, and the mobile sticky bottom bar
            (rendered separately) provides the same info on <lg. */}
        <div className="mb-4 md:mb-6 flex flex-col lg:flex-row lg:items-center lg:gap-6">
          <div className="flex items-center gap-2 md:gap-3 max-w-md lg:flex-shrink-0">
            <button
              type="button"
              onClick={() => handleTabChange('vehicle')}
              data-testid="step-1"
              className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-left ${activeTab === 'vehicle' ? 'border-cyan-500 bg-cyan-50 text-cyan-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
            >
              <span className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                activeTab === 'vehicle'
                  ? 'bg-cyan-500 text-white'
                  : isVehicleValid
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-200 text-slate-600'
              }`}>
                {activeTab !== 'vehicle' && isVehicleValid ? <Check className="w-4 h-4" /> : 1}
              </span>
              <span className="flex flex-col leading-tight min-w-0">
                <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">{t('step_1', { defaultValue: 'Step 1' })}</span>
                <span className="text-sm font-bold truncate">{t('steps.vehicle')}</span>
              </span>
            </button>
            <div className={`w-5 md:w-8 h-0.5 ${isVehicleValid ? 'bg-cyan-500' : 'bg-slate-200'}`} />
            <button
              type="button"
              onClick={() => isVehicleValid && handleTabChange('services')}
              disabled={!isVehicleValid}
              data-testid="step-2"
              className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-left ${activeTab === 'services' ? 'border-cyan-500 bg-cyan-50 text-cyan-800' : 'border-slate-200 bg-white text-slate-600'} ${!isVehicleValid ? 'opacity-50 cursor-not-allowed' : 'hover:border-slate-300'}`}
            >
              <span className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${activeTab === 'services' ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-600'}`}>2</span>
            <span className="flex flex-col leading-tight min-w-0">
              <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">{t('step_2', { defaultValue: 'Step 2' })}</span>
              <span className="text-sm font-bold truncate">{t('steps.options')}</span>
            </span>
          </button>
          </div>
          {/* Inline Final-price bar — only on Step 2. Renders below the
              stepper on <lg (stacked flex), aligned to the right of the
              stepper on lg+ (flex-row). Same `FinalPriceBar` component
              that used to live INSIDE the Step 2 panel; moved up here
              per updated TZ. */}
          {activeTab === 'services' && (
            <div className="lg:flex-1 lg:ml-auto lg:max-w-2xl">
              <FinalPriceBar
                previewLoading={previewLoading}
                pricePreview={pricePreview}
                selectedCount={selectedOptions.length}
                finalTotal={effectiveTotal}
                standardPrice={standardPrice}
                balanceBefore={typeof user?.balance === 'number' ? user.balance : null}
                inline
                isAdmin={isAdmin}
              />
            </div>
          )}
        </div>
        <TabsList className="sr-only">
          <TabsTrigger value="vehicle" data-testid="tab-vehicle">Vehicle</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Options</TabsTrigger>
        </TabsList>

        {/* ───────── Vehicle Tab ───────── */}
        <TabsContent value="vehicle" className="mt-0">
          {/* "Quick start from my vehicles" — Option B horizontal strip.
              Shows the user's most-ordered past vehicles as click-to-fill
              cards. The Vehicle Identification form below stays ALWAYS
              visible per TZ — the strip is purely additive. */}
          {!editingOrder && (
            <MyVehiclesStrip
              token={token}
              onFill={(v) => {
                // Order matters: vehicleType resets the lower fields in
                // its onValueChange handler, so we always set it FIRST
                // and then set the rest. The cascade-load `useEffect`s
                // pick up the change and fetch matching dropdown lists.
                if (v.vehicle_type) setVehicleType(v.vehicle_type);
                if (v.manufacturer) setManufacturer(v.manufacturer);
                if (v.model)        setModel(v.model);
                if (v.engine)       setEngine(v.engine);
                if (v.ecu_type)     setEcuType(v.ecu_type);
                // Scroll the form into view so the user immediately sees
                // the filled values without hunting for them.
                try {
                  setTimeout(() => {
                    document.querySelector('[data-testid="select-vehicle-type"]')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 50);
                } catch (e) { /* ignore */ }
              }}
            />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Identification */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-5 flex items-center gap-2">
                <Car className="w-4 h-4 text-slate-500" />{t('sections.vehicle_identification', { defaultValue: 'Vehicle Identification' })}
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">{t('fields.vehicle_type')} <span className="text-red-500">*</span></Label>
                  <Select value={vehicleType} onValueChange={(v) => { setVehicleType(v); setManufacturer(''); setModel(''); setEngine(''); setEcuType(''); setUserPrefModelFirst(false); }}>
                    <SelectTrigger data-testid="select-vehicle-type" className={`w-full bg-white h-11 rounded-lg ${!vehicleType ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={t('placeholders.select_type', { defaultValue: 'Select vehicle type...' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                      {types.map((tt2) => {
                        // Per-category headcount badge — pulled from the
                        // catalog mirror so it stays in lock-step with
                        // Phase D incremental syncs. `null` counts get
                        // hidden so the picker degrades gracefully when
                        // the count endpoint is unavailable.
                        const n = typeCounts[tt2];
                        return (
                          <SelectItem key={tt2} value={tt2}>
                            <span className="inline-flex items-center justify-between gap-3 w-full">
                              <span>{tt2}</span>
                              {Number.isFinite(n) && (
                                <span
                                  className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full tabular-nums"
                                  data-testid={`vehicle-type-count-${tt2}`}
                                >
                                  {n.toLocaleString()}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {vehicleType && Number.isFinite(typeCounts[vehicleType]) && (
                    <p
                      className="mt-1.5 text-[11px] text-slate-500 inline-flex items-center gap-1.5"
                      data-testid="vehicle-type-count-hint"
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-500" />
                      <span className="tabular-nums">{typeCounts[vehicleType].toLocaleString()}</span>
                      &nbsp;{t('cascade.vehicles_available_in')}&nbsp;<b>{vehicleType}</b>
                    </p>
                  )}
                  {!vehicleType && (
                    <p className="mt-1.5 text-[11px] text-slate-400 inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('cascade.vt_hint_pick_type')}
                    </p>
                  )}
                </div>
                {vehicleType && (
                <div data-testid="manufacturer-row" className="animate-fade-slide-in">
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    {t('fields.manufacturer')} <span className="text-red-500">*</span>
                  </Label>
                  <Select value={manufacturer} onValueChange={(v) => { setManufacturer(v); setModel(''); setEngine(''); setEcuType(''); }} disabled={!vehicleType}>
                    <SelectTrigger data-testid="select-manufacturer" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!manufacturer && vehicleType ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={t('placeholders.select_manufacturer', { defaultValue: 'Select manufacturer...' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-80">
                      {manufacturers.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {!manufacturer && (
                    <p className="mt-1.5 text-[11px] text-slate-400 inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('cascade.mfr_hint_filtered', { type: String(vehicleType).toLowerCase() })}
                    </p>
                  )}
                </div>
                )}
                {manufacturer && !smartOpen && (
                <div data-testid="model-row" className="animate-fade-slide-in">
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    {t('fields.model')} <span className="text-red-500">*</span>
                  </Label>
                  <Select value={model} onValueChange={(v) => { setModel(v); setEngine(''); setEcuType(''); }} disabled={!manufacturer || smart || smartOpen}>
                    <SelectTrigger data-testid="select-model" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!model && manufacturer && !smart && !smartOpen ? 'border-red-200' : 'border-slate-200'} ${smartOpen ? 'border-dashed' : ''}`}>
                      <SelectValue placeholder={smartOpen ? t('cascade.model_will_be_filled') : (smart ? '(Smart Vehicle Request below)' : t('placeholders.select_model', { defaultValue: 'Select model...' }))} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-60">
                      {models.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {!model && !smartOpen && (
                    <div
                      className="mt-1 text-[11px] text-slate-500 leading-tight"
                      data-testid="ecu-finder-hint-row"
                    >
                      {ecuFinderHint && ecuFinderHint.trim()
                        ? <SafeHtml as="span" html={ecuFinderHint} data-testid="ecu-finder-hint-content" />
                        : <span>{t('cascade.model_hint_filtered', { brand: manufacturer, type: String(vehicleType).toLowerCase() })}</span>}
                    </div>
                  )}

                  {/* Variant-B: user reached this dropdown via the escape
                      link ("I don't know the ECU"). Offer a compact
                      return path back to the ECU-first flow now that
                      they've seen the model list. Only shown while no
                      model is picked yet — otherwise the "Change via
                      ECU finder" follow-up link below takes over. */}
                  {!smart && !model && userPrefModelFirst && (
                    <div className="mt-2 flex justify-end animate-fade-slide-in" data-testid="ecu-return-link-wrap">
                      <button
                        type="button"
                        onClick={() => { setUserPrefModelFirst(false); setSmartOpen(true); setSubEcu(''); setSubModels([]); }}
                        data-testid="ecu-return-link"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 hover:text-cyan-900 hover:underline"
                      >
                        <Search className="w-3 h-3" />
                        {t('ecu_finder.back_to_ecu_first', { defaultValue: 'Actually, I do know the ECU — go back to ECU search' })}
                      </button>
                    </div>
                  )}
                  {!smart && !smartOpen && model && (
                    <div className="mt-1.5 flex justify-end animate-fade-slide-in" data-testid="ecu-change-link-wrap">
                      <button
                        type="button"
                        onClick={() => { setSmartOpen(true); setSubEcu(''); setSubModels([]); }}
                        data-testid="ecu-change-link"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 hover:text-cyan-900 hover:underline whitespace-nowrap"
                      >
                        <Search className="w-3 h-3" />
                        {t('ecu_finder.change_via_finder')}
                      </button>
                    </div>
                  )}
                </div>
                )}
                {model && (
                <div data-testid="engine-row" className="animate-fade-slide-in">
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    {t('fields.engine')} <span className="text-red-500">*</span>
                  </Label>
                  <Select value={engine} onValueChange={(v) => { setEngine(v); setEcuType(''); }} disabled={!model}>
                    <SelectTrigger data-testid="select-engine" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!engine && model ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={t('placeholders.select_engine', { defaultValue: 'Select engine...' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-60">
                      {engines.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {engine && (
                <div data-testid="ecu-row" className="animate-fade-slide-in">
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    {t('fields.ecu_type')} <span className="text-red-500">*</span>
                  </Label>
                  <Select value={ecuType} onValueChange={setEcuType} disabled={!engine || smart}>
                    <SelectTrigger data-testid="select-ecu" className={`w-full bg-white h-11 rounded-lg disabled:opacity-50 ${!ecuType && engine && !smart ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={ecus.length ? t('placeholders.select_ecu', { defaultValue: 'Select ECU...' }) : t('placeholders.no_ecu_found', { defaultValue: 'No ECU found' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                      {ecus.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {/* Progressive-disclosure placeholder — replaces the column of
                    disabled fields. Shows up only while the cascade is
                    incomplete AND the Find-by-ECU helper isn't open (the
                    helper takes the visual focus on its own). Disappears
                    once all five left-block fields are filled. */}
                {(() => {
                  if (smartOpen) return null;
                  if (vehicleType && manufacturer && model && engine && ecuType) return null;
                  // Compute the next-to-fill field for a friendly message.
                  let nextHint = null;
                  if (!vehicleType) {
                    nextHint = t('cascade.hint_no_vehicle_type');
                  } else if (!manufacturer) {
                    nextHint = t('cascade.hint_no_manufacturer');
                  } else if (!model) {
                    nextHint = t('cascade.hint_no_model');
                  } else if (!engine) {
                    nextHint = t('cascade.hint_no_engine');
                  } else if (!ecuType) {
                    nextHint = t('cascade.hint_no_ecu');
                  }
                  return (
                    <div
                      data-testid="cascade-placeholder"
                      className="rounded-lg border-2 border-dashed border-slate-200 px-4 py-6 text-center text-slate-400 text-xs flex flex-col items-center gap-2 leading-snug"
                    >
                      <ArrowUp className="w-4 h-4" />
                      <p className="max-w-[26rem]">{nextHint}</p>
                    </div>
                  );
                })()}
              </div>

              {/* ── Find your model by ECU (3-step helper, spec §6) ───────
                  Inline panel below the cascade. The user (a) picks the
                  ECU they can read on the physical control unit; (b) we
                  list models from the catalog that ship with that ECU
                  for the chosen vehicle_type + brand; (c) we list
                  engines that ship with the (model + ECU) combination.
                  Once a model + engine are resolved (manually or by
                  auto-resolution when count == 1) we show a green
                  "Apply and continue" summary card — clicking it writes
                  the trio (model + engine + ecuType) into the main form
                  and closes the panel. Nothing is written to the main
                  form until Apply is clicked, preserving reversibility. */}
              {smartOpen && (
                <div className="mt-4 p-4 rounded-xl bg-cyan-50 border border-cyan-200 animate-fade-slide-in" data-testid="smart-panel">
                  {/* ── Header: title + close × ── */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-cyan-600 text-white flex items-center justify-center flex-shrink-0">
                        <Search className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-cyan-900 leading-tight">{t('ecu_finder.title')}</h4>
                        <p className="text-[11px] text-cyan-800/80 leading-snug mt-0.5">
                          {t('ecu_finder.subtitle_short')}
                          {!manufacturer && <em className="ml-1">{t('ecu_finder.pick_manufacturer_first')}</em>}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSmartOpen(false);
                        setSubEcu(''); setSubModels([]); setSubModel('');
                        setSubEngines([]); setSubEngine('');
                      }}
                      data-testid="smart-cancel"
                      aria-label={t('ecu_finder.close_aria')}
                      className="w-7 h-7 rounded-full text-cyan-700 hover:bg-cyan-100 flex items-center justify-center flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* ── Filter context pill (Car + BMW) ── */}
                  {manufacturer && (
                    <div
                      data-testid="ecu-finder-context-pill"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-cyan-200 text-[11px] text-cyan-900 mb-3 flex-wrap"
                    >
                      <SettingsIcon className="w-3 h-3 text-cyan-700" />
                      <span>{t('ecu_finder.context_for')}&nbsp;</span>
                      <span className="px-1.5 py-0.5 rounded bg-cyan-100 font-medium">{vehicleType}</span>
                      <span>&nbsp;+&nbsp;</span>
                      <span className="px-1.5 py-0.5 rounded bg-cyan-100 font-medium">{manufacturer}</span>
                    </div>
                  )}

                  {/* ── Edge case §6.11: zero ECUs configured for this brand ── */}
                  {!smartEcusLoading && manufacturer && smartBrandEcus.length === 0 ? (
                    <div
                      data-testid="ecu-finder-zero-state"
                      className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-[12px] text-amber-900"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                        <div>
                          <div className="font-semibold mb-1">{t('ecu_finder.zero_title')}</div>
                          <p className="leading-snug mb-2">
                            {t('ecu_finder.zero_body', { type: vehicleType, brand: manufacturer })}
                          </p>
                          <ul className="list-disc ml-4 space-y-0.5 text-amber-900/85">
                            <li>{t('ecu_finder.zero_tip_vt')}</li>
                            <li>{t('ecu_finder.zero_tip_brand')}</li>
                            <li>
                              {t('ecu_finder.zero_tip_contact_pre')}
                              <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = '#/tickets'; }} className="underline">{t('ecu_finder.zero_tip_contact_link')}</a>
                              {t('ecu_finder.zero_tip_contact_post')}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* ── Step 1: pick the ECU ── */}
                      <div data-testid="ecu-finder-step-1">
                        <div className="flex items-center justify-between mb-1.5">
                          <Label className="text-[12px] font-semibold text-slate-700 flex items-center gap-2 mb-0">
                            <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${subEcu ? 'bg-emerald-500 text-white' : 'bg-cyan-600 text-white'}`}>
                              {subEcu ? <Check className="w-3 h-3" /> : '1'}
                            </span>
                            {t('ecu_finder.step1_title')}
                          </Label>
                          {!subEcu && smartBrandEcus.length > 0 && (
                            <span className="text-[10px] font-medium text-cyan-700 tabular-nums">
                              {t('ecu_finder.ecus_available', { count: smartBrandEcus.length })}
                            </span>
                          )}
                        </div>
                        <select
                          value={subEcu}
                          onChange={(e) => {
                            setSubEcu(e.target.value);
                            setSubModel(''); setSubEngines([]); setSubEngine('');
                          }}
                          disabled={smartEcusLoading || !manufacturer}
                          data-testid="sub-ecu"
                          className="w-full h-10 px-2.5 text-sm rounded-md border border-cyan-300 bg-white disabled:opacity-50"
                        >
                          <option value="">
                            {smartEcusLoading ? t('ecu_finder.loading_ecus') : t('ecu_finder.select_ecu_simple')}
                          </option>
                          {smartBrandEcus.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                      </div>

                      {/* ── Step 2: pick the matching model ── */}
                      {subEcu && (() => {
                        const single = subModels.length === 1;
                        return (
                          <div data-testid="ecu-finder-step-2" className="animate-fade-slide-in">
                            <div className="flex items-center justify-between mb-1.5">
                              <Label className="text-[12px] font-semibold text-slate-700 flex items-center gap-2 mb-0">
                                <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${subModel ? 'bg-emerald-500 text-white' : 'bg-cyan-600 text-white'}`}>
                                  {subModel ? <Check className="w-3 h-3" /> : '2'}
                                </span>
                                {t('ecu_finder.step2_title')}
                              </Label>
                              {!subModelsLoading && subModels.length > 0 && (
                                <span className="text-[10px] font-medium text-cyan-700 tabular-nums">
                                  {t('ecu_finder.models_use', { count: subModels.length, ecu: subEcu })}
                                </span>
                              )}
                            </div>
                            {subModelsLoading ? (
                              <div className="h-10 rounded-md bg-cyan-100/50 animate-pulse" />
                            ) : subModels.length === 0 ? (
                              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                {t('ecu_finder.no_models_hint', { brand: manufacturer })}
                              </div>
                            ) : single ? (
                              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2">
                                <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                <span>{t('ecu_finder.only_model_prefix')}<b>{subModels[0]}</b></span>
                              </div>
                            ) : (
                              <select
                                value={subModel}
                                onChange={(e) => setSubModel(e.target.value)}
                                data-testid="sub-model"
                                className="w-full h-10 px-2.5 text-sm rounded-md border border-cyan-300 bg-white"
                              >
                                <option value="">{t('ecu_finder.select_model_simple')}</option>
                                {subModels.map(m => <option key={m} value={m}>{m}</option>)}
                              </select>
                            )}
                          </div>
                        );
                      })()}

                      {/* ── Step 3: pick the engine ── */}
                      {subModel && (() => {
                        const single = subEngines.length === 1;
                        return (
                          <div data-testid="ecu-finder-step-3" className="animate-fade-slide-in">
                            <div className="flex items-center justify-between mb-1.5">
                              <Label className="text-[12px] font-semibold text-slate-700 flex items-center gap-2 mb-0">
                                <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${subEngine ? 'bg-emerald-500 text-white' : 'bg-cyan-600 text-white'}`}>
                                  {subEngine ? <Check className="w-3 h-3" /> : '3'}
                                </span>
                                {t('ecu_finder.step3_title')}
                              </Label>
                              {!subEnginesLoading && subEngines.length > 0 && (
                                <span className="text-[10px] font-medium text-cyan-700 tabular-nums">
                                  {t('ecu_finder.engines_match', { count: subEngines.length })}
                                </span>
                              )}
                            </div>
                            {subEnginesLoading ? (
                              <div className="space-y-1.5">
                                <div className="h-10 rounded-md bg-cyan-100/50 animate-pulse" />
                                <div className="h-10 rounded-md bg-cyan-100/30 animate-pulse" />
                              </div>
                            ) : subEngines.length === 0 ? (
                              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                {t('ecu_finder.no_engines_match')}
                              </div>
                            ) : single ? (
                              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2">
                                <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                <span>{t('ecu_finder.only_engine_prefix')}<b>{subEngines[0]}</b></span>
                              </div>
                            ) : (
                              <div
                                role="radiogroup"
                                aria-label="Engine selection"
                                data-testid="sub-engine-radio-group"
                                className="space-y-1.5 max-h-56 overflow-y-auto pr-1"
                              >
                                {subEngines.map((eng) => {
                                  const checked = subEngine === eng;
                                  return (
                                    <button
                                      type="button"
                                      key={eng}
                                      role="radio"
                                      aria-checked={checked}
                                      onClick={() => setSubEngine(eng)}
                                      data-testid={`sub-engine-${eng}`}
                                      className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md border text-[12px] transition-colors ${
                                        checked
                                          ? 'bg-cyan-100 border-cyan-400 text-cyan-900 font-medium'
                                          : 'bg-white border-cyan-200 text-slate-700 hover:bg-cyan-50'
                                      }`}
                                    >
                                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-cyan-600' : 'border-slate-300'}`}>
                                        {checked && <span className="w-1.5 h-1.5 rounded-full bg-cyan-600" />}
                                      </span>
                                      <span className="truncate">{eng}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ── Auto-fill summary card (Apply and continue) ── */}
                      {subEcu && subModel && subEngine && (
                        <div
                          data-testid="ecu-finder-summary"
                          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 animate-fade-slide-in"
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <Wand2 className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                            <div className="text-[12px] text-emerald-900 leading-snug">
                              {subModels.length === 1 && subEngines.length === 1
                                ? t('ecu_finder.ready_to_fill_single')
                                : t('ecu_finder.ready_to_fill')}
                            </div>
                          </div>
                          <div className="text-[11px] text-emerald-900/90 space-y-0.5 mb-3 pl-6">
                            <div><span className="font-semibold">{t('ecu_finder.summary_ecu')}</span> {subEcu}</div>
                            <div><span className="font-semibold">{t('ecu_finder.summary_model')}</span> {subModel}</div>
                            <div><span className="font-semibold">{t('ecu_finder.summary_engine')}</span> {subEngine}</div>
                          </div>
                          <button
                            type="button"
                            data-testid="ecu-finder-apply"
                            onClick={() => {
                              setModels(prev => prev.includes(subModel) ? prev : [...prev, subModel]);
                              setEngines(prev => prev.includes(subEngine) ? prev : [...prev, subEngine]);
                              setEcus(prev => (Array.isArray(prev) && prev.includes(subEcu)) ? prev : [...(prev || []), subEcu]);
                              setModel(subModel);
                              setEngine(subEngine);
                              setEcuType(subEcu);
                              // Telemetry — sticky flag for the lifetime of
                              // this form session. Forwarded to the backend
                              // as `created_via_ecu_finder` on submit.
                              setUsedEcuFinder(true);
                              const appliedModel = subModel;
                              const appliedEcu = subEcu;
                              setSmartOpen(false);
                              setSubEcu(''); setSubModels([]); setSubModel('');
                              setSubEngines([]); setSubEngine('');
                              setTimeout(() => {
                                const el = document.querySelector('[data-testid="select-model"]');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }, 80);
                              import('sonner').then(({ toast }) => {
                                toast.success(t('ecu_finder.applied_toast', { model: appliedModel, ecu: appliedEcu }), { duration: 5000 });
                              });
                            }}
                            className="w-full h-10 rounded-md bg-emerald-600 text-white font-medium text-[13px] hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-1.5"
                          >
                            <Check className="w-4 h-4" /> {t('ecu_finder.apply')}
                          </button>
                        </div>
                      )}

                      {/* Variant-B escape hatch. Tiny link at the bottom
                          of the helper — only shown before a full trio
                          is picked (once the customer has resolved ECU
                          + model + engine, they're on the golden path
                          and don't need an exit). Clicking latches
                          `userPrefModelFirst=true` for this session so
                          the helper doesn't auto-reopen every time
                          they tweak the manufacturer, and reveals the
                          classic Model dropdown. */}
                      {!(subEcu && subModel && subEngine) && (
                        <div className="pt-1 text-center" data-testid="ecu-finder-escape-wrap">
                          <button
                            type="button"
                            onClick={() => {
                              setSmartOpen(false);
                              setUserPrefModelFirst(true);
                              setSubEcu(''); setSubModels([]); setSubModel('');
                              setSubEngines([]); setSubEngine('');
                            }}
                            data-testid="ecu-finder-escape-link"
                            className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline"
                          >
                            {t('ecu_finder.escape_pick_by_model', { defaultValue: "I don't know the ECU — pick model manually" })}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Technical Details */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-5 flex items-center gap-2">
                <Gauge className="w-4 h-4 text-slate-500" />{t('sections.technical_details', { defaultValue: 'Technical Details' })}
              </h3>
              {/* §5.2 — VIN shortcut callout, promoted to the top of the
                  right block. Visually distinct accent card so customers
                  with a VIN can skip the whole cascade in one shot. The
                  underlying decode logic + payload is unchanged. */}
              <div
                data-testid="vin-callout"
                className="mb-5 rounded-lg border border-cyan-200 bg-cyan-50 p-3.5"
              >
                <div className="flex items-center gap-1.5 text-cyan-800 font-semibold text-[12px]">
                  <Zap className="w-3.5 h-3.5" />
                  {t('vin_callout.title')}
                </div>
                <p className="mt-1 text-[11px] text-cyan-900/85 leading-relaxed">
                  {t('vin_callout.description')}
                </p>
                <div className="mt-2.5 flex flex-col sm:flex-row gap-2">
                  <Input
                    data-testid="input-vin"
                    type="text"
                    placeholder={t('placeholders.enter_vin', { defaultValue: 'Enter VIN (17 chars)…' })}
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    className="bg-white border-cyan-300 h-9 rounded-md font-mono text-xs flex-1 focus-visible:ring-cyan-300"
                    maxLength={17}
                  />
                  <button
                    type="button"
                    data-testid="btn-decode-vin"
                    onClick={handleDecodeVin}
                    disabled={decodingVin || !vin}
                    className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md bg-cyan-600 text-white font-medium text-xs hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title={t('decode_vin_title', { defaultValue: 'Decode VIN and auto-fill vehicle fields' })}
                  >
                    {decodingVin ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    <span>{decodingVin ? t('decoding', { defaultValue: 'Decoding…' }) : t('decode', { defaultValue: 'Decode' })}</span>
                  </button>
                </div>
                {vinMessage && (
                  <div
                    data-testid="vin-message"
                    className={`mt-2 text-[11px] rounded-md px-2.5 py-1.5 border ${
                      vinMessage.tone === 'ok'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : vinMessage.tone === 'warn'
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {vinMessage.text}
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">{t('fields.gearbox_type', { defaultValue: 'Gearbox Type' })} <span className="text-red-500">*</span></Label>
                  <Select value={gearbox} onValueChange={setGearbox}>
                    <SelectTrigger data-testid="select-gearbox" className={`w-full bg-white h-11 rounded-lg ${!gearbox ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={t('placeholders.select_gearbox', { defaultValue: 'Select gearbox...' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                      {gearboxTypes.map((g) => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">{t('fields.engine_power', { defaultValue: 'Engine Power' })} <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <Input data-testid="input-hp" type="number" placeholder={t('placeholders.enter_power', { defaultValue: 'enter power...' })} value={enginePower.hp} onChange={(e) => handlePowerChange('hp', e.target.value)} className={`bg-white h-11 rounded-lg pr-10 ${!enginePower.hp ? 'border-red-200' : 'border-slate-200'}`} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">HP</span>
                    </div>
                    <div className="relative">
                      <Input data-testid="input-kw" type="number" placeholder={t('placeholders.enter_power', { defaultValue: 'enter power...' })} value={enginePower.kw} onChange={(e) => handlePowerChange('kw', e.target.value)} className="bg-white border-slate-200 h-11 rounded-lg pr-10" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">kW</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 block">{t('fields.model_year')} <span className="text-red-500">*</span></Label>
                  <Select value={modelYear} onValueChange={setModelYear}>
                    <SelectTrigger data-testid="select-year" className={`w-full bg-white h-11 rounded-lg ${!modelYear ? 'border-red-200' : 'border-slate-200'}`}>
                      <SelectValue placeholder={t('placeholders.select_year', { defaultValue: 'Select year...' })} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg max-h-60">
                      {MODEL_YEARS.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {missingFields.length > 0 && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-700">{t('validation.fill_required')}</p>
                      <p className="text-sm text-red-600 mt-1">{missingFields.join(', ')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected vehicle preview — shows the customer EXACTLY which
                  vehicle they're about to price, and confirms the form is
                  complete, before they jump to step 2. Replaces the silent
                  "all good" state with an explicit positive confirmation. */}
              {isVehicleValid && (
                <div className="mt-5 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-3.5" data-testid="selected-vehicle-preview">
                  <div className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">{t('selected_vehicle', { defaultValue: 'Selected vehicle' })}</p>
                      <p className="text-sm font-bold text-slate-800 mt-0.5 truncate">
                        {[manufacturer, model, engine].filter(Boolean).join(' · ')}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        <span className="font-medium">{ecuType}</span>
                        {gearbox && <> · {gearbox}</>}
                        {(enginePower.hp || enginePower.kw) && <> · {enginePower.hp ? `${enginePower.hp} HP` : `${enginePower.kw} kW`}</>}
                        {modelYear && <> · {modelYear}</>}
                        {vin && <> · VIN {vin}</>}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-slate-100">
                <button
                  data-testid="btn-to-services"
                  onClick={() => setActiveTab('services')}
                  disabled={!isVehicleValid}
                  className="w-full flex items-center justify-center gap-2 bg-cyan-500 text-white font-semibold px-6 py-3 rounded-lg hover:bg-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('steps.go_to_options')}<ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ───────── Services Tab ───────── */}
        <TabsContent value="services" className="mt-0">
          {/* Top FinalPriceBar moved to the stepper row above (per
              updated TZ — aligned right of the stepper on lg+). */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-24 lg:pb-0">
            {/* Left: Services + File */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-base font-semibold text-slate-800 mb-4">{t('select_services', { defaultValue: 'Select Services' })}</h3>
                {tuningList.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 text-sm">
                    {t('no_pricing', { defaultValue: 'No pricing available for this vehicle. Pick a different vehicle or contact support.' })}
                  </p>
                ) : (
                  <div className="space-y-0 divide-y divide-slate-100 max-h-[420px] overflow-y-auto pr-1">
                    {engineList.map((option) => {
                      const isSelected = selectedOptions.includes(option.id);
                      return (
                        <div key={option.id} className="flex items-center gap-3 py-3">
                          <OptionIcon optionKey={option.id} label={option.icon} selected={isSelected} />
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm ${isSelected ? 'text-cyan-600' : 'text-slate-700'}`}>{option.name}</div>
                          </div>
                          <span className={`text-base font-semibold mr-2 ${isSelected ? 'text-cyan-600' : 'text-slate-800'}`}>{option.price}€</span>
                          <Switch data-testid={`option-${option.id}`} checked={isSelected} onCheckedChange={() => toggleOption(option.id)} className="data-[state=checked]:bg-cyan-500" />
                        </div>
                      );
                    })}
                    {/* Gearbox (TCU) options render after the main list
                        with an explicit subheading so customers see at
                        a glance that these are transmission services,
                        not engine ECU services. Hidden entirely when
                        the vehicle has no TCU-tagged options. */}
                    {gearboxList.length > 0 && (
                      <>
                        <div className="pt-4 pb-1 mt-2 border-t-2 border-slate-200 text-center" data-testid="gearbox-options-heading">
                          <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-900">
                            Gearbox tuning options
                          </h4>
                        </div>
                        {gearboxList.map((option) => {
                          const isSelected = selectedOptions.includes(option.id);
                          return (
                            <div key={option.id} className="flex items-center gap-3 py-3">
                              <OptionIcon optionKey={option.id} label={option.icon} selected={isSelected} />
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium text-sm ${isSelected ? 'text-violet-700' : 'text-slate-700'}`}>{option.name}</div>
                              </div>
                              <span className={`text-base font-semibold mr-2 ${isSelected ? 'text-violet-700' : 'text-slate-800'}`}>{option.price}€</span>
                              <Switch data-testid={`option-${option.id}`} checked={isSelected} onCheckedChange={() => toggleOption(option.id)} className="data-[state=checked]:bg-violet-500" />
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* File Upload — visually amplified when a service is
                  selected but no file uploaded yet, since file is the
                  #1 missed required field per support tickets. */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />{t('file_settings', { defaultValue: 'File Settings' })}
                </h3>
                <div
                  className={`upload-zone text-center cursor-pointer rounded-lg mb-2 transition-all ${isDragOver ? 'dragover' : ''} ${
                    !uploadedFile && selectedOptions.length > 0
                      ? 'border-2 border-dashed border-amber-400 bg-amber-50 hover:bg-amber-100 p-6'
                      : uploadedFile
                        ? 'border border-emerald-300 bg-emerald-50/40 p-3'
                        : 'border-2 border-dashed border-slate-300 hover:border-slate-400 p-5'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleFileDrop}
                  onClick={() => document.getElementById('file-input').click()}
                  data-testid="upload-zone"
                >
                  <input id="file-input" type="file" className="hidden" onChange={handleFileDrop} />
                  {uploadedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span className="text-emerald-700 text-sm font-medium truncate max-w-[200px]">{uploadedFile.name}</span>
                      <span className="text-[10px] text-emerald-600/80">· click to replace</span>
                    </div>
                  ) : isEditMode && editingOrder?.original_file_name ? (
                    <div className="flex items-center justify-center gap-2 text-cyan-700 text-xs" data-testid="edit-existing-file">
                      <Check className="w-4 h-4 text-cyan-600" />
                      <span className="font-medium truncate max-w-[220px]">Using existing: {editingOrder.original_file_name}</span>
                      <span className="text-slate-400">· click to replace</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <Upload className={`w-6 h-6 ${selectedOptions.length > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
                      <span className={`text-sm font-medium ${selectedOptions.length > 0 ? 'text-amber-700' : 'text-slate-600'}`}>
                        {selectedOptions.length > 0 ? t('file_upload.upload_required', { defaultValue: 'Upload your ECU file — required' }) : t('file_upload.drop_here', { defaultValue: 'Drop file here or click to browse' })}
                      </span>
                      <span className="text-[11px] text-slate-400">Any file type · .bin, .ori, .zip, .rar and others</span>
                    </div>
                  )}
                </div>
                {!uploadedFile && selectedOptions.length > 0 && !(isEditMode && editingOrder?.original_file_name) && (
                  <p className="text-[11px] text-amber-700 mb-3 flex items-center gap-1" data-testid="hint-upload-required">
                    <AlertCircle className="w-3 h-3" /> {t('file_upload.required')}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Select value={readingMethod} onValueChange={setReadingMethod}>
                      <SelectTrigger data-testid="select-reading-method" className={`w-full bg-white h-9 rounded-lg text-xs ${!readingMethod && selectedOptions.length > 0 ? 'border-amber-400' : 'border-slate-200'}`}>
                        <SelectValue placeholder={`${t('fields.reading_method')} *`} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                        {readingMethods.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {!readingMethod && selectedOptions.length > 0 && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid="hint-reading-method-required">
                        <AlertCircle className="w-2.5 h-2.5" /> Required
                      </p>
                    )}
                  </div>
                  <div>
                    <Select value={fileType} onValueChange={setFileType}>
                      <SelectTrigger data-testid="select-file-type" className={`w-full bg-white h-9 rounded-lg text-xs ${!fileType && selectedOptions.length > 0 ? 'border-amber-400' : 'border-slate-200'}`}>
                        <SelectValue placeholder={`${t('fields.file_type')} *`} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 rounded-lg shadow-lg">
                        {FILE_TYPES.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {!fileType && selectedOptions.length > 0 && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid="hint-file-type-required">
                        <AlertCircle className="w-2.5 h-2.5" /> Required
                      </p>
                    )}
                  </div>
                </div>

                {/* Optional customer note (description) — collapsible, lives
                    inside the File Settings card so step 2 stays compact. */}
                <details className="mt-3 group" data-testid="customer-note-details">
                  <summary className="flex items-center justify-between cursor-pointer select-none text-xs font-medium text-slate-600 hover:text-slate-800 px-2 py-1.5 rounded-md hover:bg-slate-50 list-none">
                    <span className="flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                      {t('description_for_engineer', { defaultValue: 'Description for the engineer' })}
                      <span className="text-[10px] font-normal text-slate-400">· {t('description_optional', { defaultValue: 'optional' })}</span>
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-2 space-y-2">
                    <textarea
                      data-testid="customer-note-textarea"
                      value={customerNote}
                      onChange={(e) => setCustomerNote(e.target.value.slice(0, 200))}
                      placeholder={t('description_placeholder', { defaultValue: 'Please provide more information for our engineer (optional, max 200 chars)…' })}
                      rows={3}
                      maxLength={200}
                      className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-700 cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          data-testid="customer-note-photo-input"
                          onChange={(e) => setNotePhoto(e.target.files?.[0] || null)}
                        />
                        <Paperclip className="w-3.5 h-3.5" />
                        {notePhoto ? (
                          <span className="text-emerald-600 font-medium truncate max-w-[140px]">{notePhoto.name}</span>
                        ) : (
                          <span>{t('attach_any_file', { defaultValue: 'Attach any file' })}</span>
                        )}
                      </label>
                      {notePhoto && (
                        <button
                          type="button"
                          onClick={() => setNotePhoto(null)}
                          className="text-[11px] text-rose-500 hover:text-rose-700"
                          data-testid="customer-note-photo-remove"
                        >
                          remove
                        </button>
                      )}
                      <span className="ml-auto text-[10px] tabular-nums text-slate-400" data-testid="customer-note-counter">
                        {customerNote.length}/200
                      </span>
                    </div>
                  </div>
                </details>
              </div>

              <button
                data-testid="btn-back"
                onClick={() => setActiveTab('vehicle')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors text-sm"
              >
                <ChevronLeft className="w-4 h-4" />{t('steps.back_to_vehicle')}
              </button>
            </div>

            {/* Middle: Instructions */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm lg:sticky lg:top-20">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <div className="w-1 h-4 bg-blue-600 rounded-full"></div>{t('instructions', { defaultValue: 'Instructions' })}
                </h3>
                {selectedOptionsInfoWithImages.length > 0 ? (
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {selectedOptionsInfoWithImages.map((option, idx) => (
                      <div key={option.id} className={idx > 0 ? 'pt-3 border-t border-slate-100' : ''}>
                        <div className="flex items-center gap-2 mb-2">
                          {OPTION_ICONS[option.id] ? (
                            <span className="w-6 h-6 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              <img src={`/icons/options/${OPTION_ICONS[option.id]}`} alt="" className="w-4 h-4 object-contain" loading="lazy" />
                            </span>
                          ) : (
                            <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{option.icon}</span>
                          )}
                          <span className="font-medium text-sm text-slate-800">{option.name}</span>
                        </div>
                        {option.instruction ? (
                          <SafeHtml as="p" className="text-slate-600 text-xs leading-relaxed [&_a]:text-cyan-600 [&_a]:underline [&_a:hover]:text-cyan-800 [&_a]:break-all" html={linkify(option.instruction)} />
                        ) : (
                          <p className="text-slate-400 text-xs italic">{t('no_instructions', { defaultValue: 'No instructions for this option.' })}</p>
                        )}
                        {option.image && (
                          <div
                            className="mt-2 relative rounded-lg overflow-hidden border border-slate-200 cursor-pointer group w-20 h-14"
                            onClick={() => setModalImage(option.image)}
                          >
                            <img src={option.image} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        )}
                        {option.pdf && (
                          <button
                            type="button"
                            onClick={() => setPdfInstruction(option.pdf)}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold hover:bg-rose-100 transition-colors"
                            data-testid={`instruction-pdf-btn-${option.id}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            {t('read_pdf_instruction', { defaultValue: 'Read PDF instruction' })}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-xs italic">{t('select_service_for_instructions', { defaultValue: 'Select a service to see instructions' })}</p>
                )}
              </div>
            </div>

            {/* Right: Priority + Summary */}
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-20 space-y-4">
                {/* Priority */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />{t('sections.priority')}
                  </h3>

                  {/* Working-hours countdown banner */}
                  {workingHoursInfo && (
                    <div data-testid="working-hours-banner" className={`mb-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2 border ${
                      workingHoursInfo.closed || workingHoursInfo.minutesToClose <= 0
                        ? 'bg-rose-50 border-rose-200 text-rose-800'
                        : workingHoursInfo.minutesToOpen > 0
                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                          : workingHoursInfo.minutesToClose <= 60
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        workingHoursInfo.closed || workingHoursInfo.minutesToClose <= 0
                          ? 'bg-rose-500'
                          : workingHoursInfo.minutesToOpen > 0 || workingHoursInfo.minutesToClose <= 60
                            ? 'bg-amber-500 animate-pulse'
                            : 'bg-emerald-500'
                      }`} />
                      {workingHoursInfo.closed ? (
                        <span><strong>{t('working_hours.closed_today', { defaultValue: 'Closed today.' })}</strong> {t('working_hours.closed_today_note', { defaultValue: 'Orders may take longer than usual.' })}</span>
                      ) : workingHoursInfo.minutesToOpen > 0 ? (
                        <Trans
                          t={t}
                          i18nKey="working_hours.opens_in"
                          ns="new-order"
                          values={{ time: fmtCountdown(workingHoursInfo.minutesToOpen), at: workingHoursInfo.open }}
                          components={{ strong: <strong /> }}
                          defaults="Opens in <strong>{{time}}</strong> at {{at}}."
                        />
                      ) : workingHoursInfo.minutesToClose <= 0 ? (
                        <span><strong>{t('working_hours.closed_for_today', { defaultValue: 'Closed for today.' })}</strong> {t('working_hours.closed_at', { at: workingHoursInfo.close, defaultValue: `Closed at ${workingHoursInfo.close}.` })}</span>
                      ) : (
                        <Trans
                          t={t}
                          i18nKey="working_hours.closes_at_in"
                          ns="new-order"
                          values={{ at: workingHoursInfo.close, time: fmtCountdown(workingHoursInfo.minutesToClose) }}
                          components={{ strong: <strong /> }}
                          defaults="Closes at <strong>{{at}}</strong> · {{time}} left."
                        />
                      )}
                    </div>
                  )}

                  <div className="space-y-2" data-testid="priority-list">
                    {(apiPriorities || []).filter((p) => p.enabled).map((p) => {
                      const isSelected = priority === p.key;
                      const unavailable = !p.available_now;
                      const surcharge = p.factor && p.factor !== 1 ? `${p.factor > 1 ? '+' : ''}${Math.round((p.factor - 1) * 100)}%` : '';
                      const fmtTime = p.eta_max >= 60
                        ? `${p.eta_min >= 60 ? (p.eta_min/60).toFixed(p.eta_min%60===0?0:1) + 'h' : p.eta_min + 'min'}–${(p.eta_max/60).toFixed(p.eta_max%60===0?0:1)}h`
                        : `${p.eta_min}–${p.eta_max} min`;
                      return (
                        <button
                          key={p.key}
                          data-testid={`priority-${p.key}`}
                          onClick={() => !unavailable && setPriority(p.key)}
                          disabled={unavailable}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left relative ${
                            unavailable
                              ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                              : isSelected
                                ? 'border-cyan-400 bg-cyan-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xl flex-shrink-0">{p.icon}</span>
                              <div className="min-w-0">
                                <span className={`font-semibold text-sm block ${isSelected ? 'text-cyan-700' : 'text-slate-700'}`}>{localisePriorityName(p)}</span>
                                <span className="text-slate-400 text-xs">{fmtTime}</span>
                              </div>
                            </div>
                            {surcharge && (
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${p.factor > 1 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'} flex-shrink-0`}>{surcharge}</span>
                            )}
                          </div>
                          {p.description && (
                            <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{localisePriorityDescription(p)}</p>
                          )}
                          {unavailable && (
                            <div className="mt-2 px-2 py-1 bg-amber-100 text-amber-800 text-[10px] font-medium rounded flex items-center gap-1" data-testid={`priority-unavailable-${p.key}`}>
                              <span className="w-1.5 h-1.5 bg-amber-600 rounded-full animate-pulse" />
                              {localiseUnavailableReason(p.unavailable_reason) || (p.offline ? (p.offline_message || t('priority_status.currently_offline', { defaultValue: 'Currently offline' })) : t('priority_status.out_of_hours', { defaultValue: 'Out of working hours' }))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                    {apiPriorities === null && (
                      <div className="flex items-center gap-2 text-xs text-slate-400 italic py-4" data-testid="priority-loading">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('priority_status.loading', { defaultValue: 'Loading priorities…' })}
                      </div>
                    )}
                    {apiPriorities && apiPriorities.length === 0 && (
                      <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="priority-empty">
                        <p className="font-semibold text-amber-800">{t('priority_status.load_failed_title', { defaultValue: "We can't load priorities right now." })}</p>
                        <p>{t('priority_status.load_failed_body', { defaultValue: "Please check your connection and refresh the page. If the problem persists, contact support — you'll still be able to place the order once this loads." })}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white shadow-sm">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-cyan-400 rounded-full"></div>{t('sections.summary', { defaultValue: 'Summary' })}
                  </h3>

                  {pricingInfo.items.length > 0 ? (
                    <>
                      <div className="space-y-2 mb-3">
                        {pricingInfo.items.map((item) => (
                          <div key={item.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {OPTION_ICONS[item.id] ? (
                                <span className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  <img src={`/icons/options/${OPTION_ICONS[item.id]}`} alt="" className="w-4 h-4 object-contain brightness-0 invert opacity-90" loading="lazy" />
                                </span>
                              ) : (
                                <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{item.icon}</span>
                              )}
                              <span className="text-slate-400 truncate text-xs">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {item.discount > 0 ? (
                                <>
                                  <span className="text-slate-500 line-through text-xs">{item.originalPrice}€</span>
                                  <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded">-{item.discount}%</span>
                                  <span className="font-semibold text-white">{item.finalPrice}€</span>
                                </>
                              ) : (
                                <span className="font-semibold">{item.finalPrice}€</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {pricingInfo.savings > 0 && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 mb-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-green-400 font-medium">You save</span>
                            <span className="text-green-400 font-bold">{pricingInfo.savings}€</span>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const apiP = (apiPriorities || []).find((x) => x.key === priority);
                        if (!apiP || !apiP.factor || apiP.factor === 1) return null;
                        const pct = Math.round((apiP.factor - 1) * 100);
                        return (
                          <div className="flex justify-between text-sm py-2 border-t border-slate-700">
                            <span className="text-slate-400">{t('fields.priority', { defaultValue: 'Priority' })} ({localisePriorityName(apiP)})</span>
                            <span className={pct > 0 ? 'text-red-400' : 'text-emerald-400'}>{pct > 0 ? `+${pct}%` : `${pct}%`}</span>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <p className="text-slate-500 text-sm mb-4">{t('no_services_selected', { defaultValue: 'No services selected' })}</p>
                  )}

                  <div className="flex justify-between items-center mb-4 pt-3 border-t border-slate-700">
                    <span className="text-slate-400 text-sm">{t('summary.total')}</span>
                    {discountInfo?.has_discount ? (
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-slate-500 line-through text-base">{totalPrice}€</span>
                          <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-black rounded-full tracking-wide">
                            {discountInfo.group_type === 'percent' ? `-${discountInfo.percent_value}%` : 'SPECIAL'}
                          </span>
                        </div>
                        <span className="text-4xl font-black text-emerald-400" data-testid="summary-final-price">{discountInfo.final}€</span>
                        <p className="text-[10px] text-emerald-400/80 mt-0.5">Your <strong className="not-italic">{discountInfo.group_name}</strong> tier is active — saved €{discountInfo.discount}</p>
                      </div>
                    ) : (
                      <span className="text-4xl font-black text-cyan-400">{totalPrice}€</span>
                    )}
                  </div>

                  {servicesMissing.length > 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-amber-400">{t('please_complete', { defaultValue: 'Please complete:' })}</p>
                          <ul className="text-xs text-amber-300/80 mt-1">
                            {servicesMissing.map((item) => (<li key={item}>• {item}</li>))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {servicesMissing.length === 0 && (!acceptTerms || !acceptRefund) && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400" />
                        <p className="text-xs text-amber-400">{t('accept_warning', { defaultValue: 'Please accept Terms and Refund policy' })}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox id="terms" data-testid="checkbox-terms" checked={acceptTerms} onCheckedChange={setAcceptTerms} className="border-slate-500" />
                      <span className="text-xs text-slate-400">{t('i_accept', { defaultValue: 'I accept' })}{' '}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalSlug('terms'); }}
                          className="text-cyan-400 hover:underline cursor-pointer"
                          data-testid="link-terms-of-service"
                        >{t('terms_of_service', { defaultValue: 'Terms of service' })}</button>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox id="refund" data-testid="checkbox-refund" checked={acceptRefund} onCheckedChange={setAcceptRefund} className="border-slate-500" />
                      <span className="text-xs text-slate-400">{t('i_accept', { defaultValue: 'I accept' })}{' '}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalSlug('refund'); }}
                          className="text-cyan-400 hover:underline cursor-pointer"
                          data-testid="link-refund-policy"
                        >{t('refund_policy', { defaultValue: 'Refund policy' })}</button>
                      </span>
                    </label>
                  </div>

                  {/* Global maintenance banner — admin disabled new orders */}
                  {globalFlags?.orders_disabled && (
                    <div className="mb-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-start gap-3" data-testid="orders-global-disabled-banner">
                      <span className="text-2xl flex-shrink-0">⚠</span>
                      <div>
                        <div className="font-bold text-amber-800 text-sm">New orders are temporarily unavailable</div>
                        <div className="text-xs text-amber-700 whitespace-pre-line mt-0.5">{globalFlags.orders_disabled_message}</div>
                      </div>
                    </div>
                  )}

                  <button
                    data-testid="btn-order"
                    disabled={!isServicesValid || submitting || (!isEditMode && !!globalFlags?.orders_disabled)}
                    onClick={handleSubmit}
                    className="w-full flex items-center justify-center gap-2 bg-cyan-500 text-white font-bold px-6 py-3 rounded-xl hover:bg-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Rocket className="w-5 h-5" />
                    {submitting
                      ? (isEditMode ? t('cta.saving', { defaultValue: 'Saving…' }) : t('cta.creating', { defaultValue: 'Creating…' }))
                      : isEditMode
                        ? t('cta.save_changes', { defaultValue: 'Save Changes' })
                        : (globalFlags?.orders_disabled ? t('cta.orders_paused', { defaultValue: 'Orders paused' }) : t('cta.order_now', { defaultValue: 'Order Now' }))}
                  </button>

                  <div className="mt-3 text-center">
                    <p className="text-xs text-slate-400">
                      {t('your_balance', { defaultValue: 'Your balance' })}: <strong className="text-white">€{user?.balance?.toFixed(2) || '0.00'}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile sticky bottom bar — visible ≤lg only. Mirrors the
              compact desktop top bar plus an action button so the
              customer can submit from one-thumb reach without scrolling
              back to the right-side Summary. */}
          <MobileStickyBar
            pricePreview={pricePreview}
            previewLoading={previewLoading}
            selectedCount={selectedOptions.length}
            finalTotal={effectiveTotal}
            standardPrice={standardPrice}
            isReady={isServicesValid && !submitting && !(!isEditMode && !!globalFlags?.orders_disabled)}
            missingCount={servicesMissing.length}
            ctaLabel={
              submitting
                ? (isEditMode ? t('cta.saving', { defaultValue: 'Saving…' }) : t('cta.creating', { defaultValue: 'Creating…' }))
                : !isServicesValid
                  ? t('cta.complete_required_fields', { defaultValue: 'Complete required fields' })
                  : isEditMode
                    ? t('cta.save_changes_mobile', { defaultValue: 'Save changes' })
                    : (globalFlags?.orders_disabled ? t('cta.orders_paused', { defaultValue: 'Orders paused' }) : t('cta.submit_order', { defaultValue: 'Submit order' }))
            }
            onSubmit={handleSubmit}
            isAdmin={isAdmin}
          />
        </TabsContent>
      </Tabs>

      {/* Image Modal — near-fullscreen so the customer can read small
          annotations on wiring / sensor photos without a save-and-open
          round trip. Click backdrop or image to close. */}
      <Dialog open={!!modalImage} onOpenChange={() => setModalImage(null)}>
        <DialogContent
          className="!max-w-[95vw] w-[95vw] p-0 overflow-hidden bg-black border-none sm:!rounded-lg"
          style={{ maxHeight: '95vh' }}
        >
          <button
            onClick={() => setModalImage(null)}
            className="absolute top-2 right-2 z-10 w-9 h-9 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80"
            data-testid="instruction-image-close-btn"
            aria-label="Close image"
          >
            <X className="w-5 h-5" />
          </button>
          {modalImage && (
            <img
              src={modalImage}
              alt="Enlarged view"
              onClick={() => setModalImage(null)}
              className="block mx-auto cursor-zoom-out"
              style={{ maxWidth: '95vw', maxHeight: '95vh', width: 'auto', height: 'auto' }}
              data-testid="instruction-image-fullsize"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PDF instruction viewer — centre-screen reader with page flipping. */}
      {pdfInstruction && (
        <PdfInstructionViewer
          url={pdfInstruction.url}
          name={pdfInstruction.name}
          onClose={() => setPdfInstruction(null)}
        />
      )}

      {/* Order created success modal — replaces the legacy alert(). */}
      {orderSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
             data-testid="order-success-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-7 text-center text-white">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-3 ring-4 ring-white/15">
                <CheckCircle2 className="w-9 h-9" />
              </div>
              <h3 className="text-xl font-bold tracking-tight">{t('success_modal.heading', { defaultValue: 'Order placed successfully' })}</h3>
              <p className="text-sm opacity-90 mt-1">{t('success_modal.sub', { defaultValue: "We've received your tuning request." })}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
                <p className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-1">{t('success_modal.order_number', { defaultValue: 'Order number' })}</p>
                <p className="text-2xl font-bold text-slate-800 font-mono" data-testid="order-success-number">№{orderSuccess.number || '—'}</p>
                <p className="text-sm text-slate-600 mt-3">
                  <Trans
                    t={t}
                    i18nKey="success_modal.reserved_balance"
                    ns="new-order"
                    values={{ amount: `€${orderSuccess.total.toFixed(2)}` }}
                    components={{ strong: <span className="font-semibold" /> }}
                    defaults="<strong>{{amount}}</strong> reserved from your balance"
                  />
                </p>
              </div>

              {/* Queue position + ETA */}
              {(orderSuccess.queuePosition != null || orderSuccess.etaMin != null || orderSuccess.etaMax != null) && (
                <div className="rounded-lg p-4 border border-cyan-100 bg-gradient-to-br from-cyan-50 to-blue-50" data-testid="order-success-queue">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase font-bold text-cyan-700 tracking-wider">{t('success_modal.in_queue', { defaultValue: 'In queue' })}</span>
                    {orderSuccess.queuePosition != null && (
                      <span className="text-xs font-bold text-cyan-800" data-testid="order-success-queue-pos">
                        {t('success_modal.position', { n: orderSuccess.queuePosition, defaultValue: `Position #${orderSuccess.queuePosition}` })}
                        {orderSuccess.queueTotal != null && orderSuccess.queueTotal !== orderSuccess.queuePosition && (
                          <span className="text-cyan-500">{t('success_modal.queue_of', { total: orderSuccess.queueTotal, defaultValue: ` of ${orderSuccess.queueTotal}` })}</span>
                        )}
                      </span>
                    )}
                  </div>
                  {/* Queue progress bar — fill = how close to the front (1 = front, queueTotal = back) */}
                  {orderSuccess.queuePosition != null && orderSuccess.queueTotal && orderSuccess.queueTotal > 0 && (() => {
                    // Visual fill: front = 100%, back = ~10%. Linearly map.
                    const pct = Math.max(8, 100 - ((orderSuccess.queuePosition - 1) / Math.max(1, orderSuccess.queueTotal)) * 100);
                    return (
                      <div className="h-2 bg-cyan-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                          data-testid="order-success-queue-bar"
                        />
                      </div>
                    );
                  })()}
                  {(orderSuccess.etaMin != null || orderSuccess.etaMax != null) && (() => {
                    // Render ETA window as a human range. Values come from
                    // the priority settings (eta_min / eta_max in minutes).
                    const fmtMin = (n) => {
                      if (n == null) return '';
                      if (n < 60) return `${n} min`;
                      const h = Math.floor(n / 60);
                      const m = n % 60;
                      return m === 0 ? `${h}h` : `${h}h ${m}m`;
                    };
                    const mn = orderSuccess.etaMin ?? orderSuccess.etaMax;
                    const mx = orderSuccess.etaMax ?? orderSuccess.etaMin;
                    const txt = mn === mx ? fmtMin(mn) : `${fmtMin(mn)} – ${fmtMin(mx)}`;
                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-cyan-800 flex items-center gap-1.5" data-testid="order-success-eta">
                          <Clock className="w-3.5 h-3.5" />
                          <Trans
                            t={t}
                            i18nKey="success_modal.estimated_ready_in"
                            ns="new-order"
                            values={{ txt }}
                            components={{ strong: <span className="font-bold" /> }}
                            defaults="Estimated ready in <strong>{{txt}}</strong>"
                          />
                        </p>
                        {orderSuccess.priorityName && (
                          <p className="text-[11px] text-cyan-700/90 flex items-center gap-1.5 pl-5" data-testid="order-success-priority">
                            <span className="opacity-70">{t('success_modal.priority_label', { defaultValue: 'Priority:' })}</span>
                            {orderSuccess.priorityIcon && <span>{orderSuccess.priorityIcon}</span>}
                            <span className="font-bold">{orderSuccess.priorityName}</span>
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <p className="text-xs text-slate-500 text-center">
                {t('success_modal.track_progress', { defaultValue: 'Track progress, chat with the engineer and download your modified file from your dashboard.' })}
              </p>
              <button
                onClick={() => {
                  setOrderSuccess(null);
                  if (onNavigate) onNavigate('dashboard');
                }}
                data-testid="order-success-ok-btn"
                className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg active:scale-[0.99] transition-all"
              >
                {t('success_modal.ok_go_dashboard', { defaultValue: 'OK · Go to dashboard' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient balance modal — mirrors the order-success style so the
          customer gets a friendly, branded CTA straight to the top-up flow
          instead of a raw browser alert. */}
      {insufficientFunds && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
             data-testid="insufficient-funds-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 py-7 text-center text-white">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-3 ring-4 ring-white/15">
                <AlertTriangle className="w-9 h-9" />
              </div>
              <h3 className="text-xl font-bold tracking-tight">{t('insufficient_modal.heading', { defaultValue: 'Not enough balance' })}</h3>
              <p className="text-sm opacity-90 mt-1">{t('insufficient_modal.sub', { defaultValue: 'Top up your account to place this order.' })}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t('insufficient_modal.order', { defaultValue: 'Order' })}</p>
                  <p className="text-base font-bold text-slate-800 font-mono mt-1" data-testid="insufficient-needed">
                    €{insufficientFunds.needed.toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t('insufficient_modal.balance', { defaultValue: 'Balance' })}</p>
                  <p className="text-base font-bold text-slate-800 font-mono mt-1" data-testid="insufficient-balance">
                    €{insufficientFunds.balance.toFixed(2)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">{t('insufficient_modal.top_up_label', { defaultValue: 'Top up' })}</p>
                  <p className="text-base font-bold text-amber-700 font-mono mt-1" data-testid="insufficient-shortfall">
                    €{insufficientFunds.shortfall.toFixed(2)}
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">
                {t('insufficient_modal.saved_locally', { defaultValue: "Your order details are saved locally — after top-up you'll return here with everything pre-filled." })}
              </p>

              {/* Editable top-up amount — defaults to the exact shortfall but
                  customers can over-fund (e.g. cover several future orders). */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="block text-[10px] uppercase font-bold text-amber-700 tracking-wider mb-1.5">
                  {t('insufficient_modal.amount_to_topup', { defaultValue: 'Amount to top up' })}
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-extrabold text-amber-700">€</span>
                  <input
                    type="number"
                    min={insufficientFunds.shortfall}
                    step="0.01"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    className="flex-1 bg-white border border-amber-300 rounded-md px-3 py-2 text-2xl font-extrabold text-slate-800 font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="insufficient-topup-amount"
                  />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[insufficientFunds.shortfall, Math.ceil(insufficientFunds.shortfall / 10) * 10, Math.ceil(insufficientFunds.shortfall / 50) * 50, Math.ceil(insufficientFunds.shortfall / 100) * 100]
                    .filter((v, i, arr) => v >= insufficientFunds.shortfall && arr.indexOf(v) === i)
                    .slice(0, 4)
                    .map((v) => (
                      <button
                        key={v}
                        onClick={() => setTopupAmount(v.toFixed(2))}
                        className={`flex-1 px-2 py-1 rounded-md text-xs font-bold border transition ${
                          parseFloat(topupAmount) === v ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'
                        }`}
                      >€{v.toFixed(0)}</button>
                    ))}
                </div>
                <p className="text-[10px] text-amber-700 mt-1.5">
                  <Trans
                    t={t}
                    i18nKey="insufficient_modal.minimum_to_cover"
                    ns="new-order"
                    values={{ amount: `€${insufficientFunds.shortfall.toFixed(2)}` }}
                    components={{ b: <b /> }}
                    defaults="Minimum: <b>{{amount}}</b> to cover this order."
                  />
                </p>
              </div>

              <button
                onClick={async () => {
                  const amt = Math.max(parseFloat(topupAmount) || 0, insufficientFunds.shortfall);
                  // Persist pending order state so RechargeAccount → return
                  // can rehydrate the form and let the customer re-submit
                  // in one click instead of starting from scratch.
                  try {
                    sessionStorage.setItem('pendingOrder', JSON.stringify({
                      vehicleType, manufacturer, model, engine,
                      ecuType, gearbox,
                      enginePower, modelYear, vin, fileType, readingMethod,
                      selectedOptions: Array.from(selectedOptions || []),
                      priority,
                      acceptTerms, acceptRefund,
                      customerNote,
                      // Filename only (the actual blob lives in IndexedDB to
                      // survive the recharge navigation — see below).
                      uploadedFile: uploadedFile ? { name: uploadedFile.name, size: uploadedFile.size } : null,
                      notePhoto:    notePhoto    ? { name: notePhoto.name,    size: notePhoto.size }    : null,
                      ts: Date.now(),
                    }));
                    sessionStorage.setItem('pendingTopupAmount', String(amt));
                    // Persist the actual File blobs in IndexedDB so the
                    // customer doesn't have to re-attach them after payment.
                    await Promise.all([
                      uploadedFile ? savePendingFile('uploadedFile', uploadedFile) : Promise.resolve(),
                      notePhoto    ? savePendingFile('notePhoto',    notePhoto)    : Promise.resolve(),
                    ]);
                  } catch (e) {
                    // Surface the failure — silent crashes were the original
                    // root cause of the "form forgot everything" bug.
                    console.warn('[NewOrderWithPricing] save pending failed', e);
                  }
                  setInsufficientFunds(null);
                  if (onNavigate) onNavigate('recharge');
                }}
                data-testid="insufficient-funds-topup-btn"
                className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-center gap-2"
              >
                <Wallet className="w-4 h-4" />
                {t('insufficient_modal.top_up_btn', { amount: `€${(parseFloat(topupAmount) || insufficientFunds.shortfall).toFixed(2)}`, defaultValue: `Top up €${(parseFloat(topupAmount) || insufficientFunds.shortfall).toFixed(2)}` })}
              </button>
              <button
                onClick={() => setInsufficientFunds(null)}
                data-testid="insufficient-funds-close-btn"
                className="w-full px-4 py-2.5 text-slate-600 hover:text-slate-800 text-sm font-semibold transition-colors"
              >
                {t('insufficient_modal.keep_editing', { defaultValue: 'Keep editing' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legal document popup — opened from "Terms of service" / "Refund
          policy" links next to the acceptance checkboxes. */}
      <LegalDocModal
        slug={legalSlug}
        open={!!legalSlug}
        onClose={() => setLegalSlug(null)}
      />
    </div>
  );
}
